import { MAIN_AREAS, SHELF_ZONES, type MainAreaId } from "../../lib/shelfLocations";
export { MAIN_AREAS, SHELF_ZONES, type MainAreaId, type MainAreaConfig, type ZoneConfig } from "../../lib/shelfLocations";
import { useEffect, useState, useMemo, type CSSProperties } from "react";
import { supabase } from "../../lib/supabase";
import { readAllRows } from "../../lib/readAllRows";
import {
  updateBottleShelfLocation,
  insertCustomMenuItem,
  batchAuditShelfSlot,
  listShelfAuditHistory,
  smartMoveShelfBottle,
  lookupBottleByBarcode,
  forceSquareCatalogSync,
  type ShelfAuditRecord,
} from "../../lib/api";
import { whoami } from "../../lib/auth";
import type { SessionUser } from "../../types/auth";
import RecipeStudioTabs from "../../components/admin/RecipeStudioTabs";
import BarcodeScannerModal from "../../components/common/BarcodeScannerModal";
import BottlePourCount from "../../components/admin/BottlePourCount";
import { useIsMobile } from "../../lib/useIsMobile";

export interface ShelfBottleVariation {
  id: string;
  size_label: string;
  upc?: string | null;
  sku?: string | null;
  bottle_price_cents?: number | null;
  pour_price_cents?: number | null;
  is_default?: boolean;
}

export interface ShelfBottle {
  id: string;
  name: string;
  distillery: string | null;
  category: string | null;
  shelf_location: string[];
  retail_price_cents?: number | null;
  pour_price_cents?: number | null;
  inventory_count?: number | null;
  upc?: string | null;
  sku?: string | null;
  variations?: ShelfBottleVariation[];
}

/** "Name - N oz" menu_items are Square pour order options, not bottles on a shelf. */
const POUR_ROW_NAME = /\s*-\s*\d+(\.\d+)?\s*oz$/i;
const isPourMatch = (item: any) => POUR_ROW_NAME.test(((item?.bottle || item)?.name || "").trim());

export function normalizeBarcodeString(code: string | null | undefined): string {
  if (!code) return "";
  return String(code).trim().replace(/[\s-_]/g, "").toLowerCase();
}

export function getBarcodeVariantsList(code: string): string[] {
  const clean = normalizeBarcodeString(code);
  if (!clean) return [];
  const variants = new Set<string>();
  variants.add(clean);

  // Strip leading zeroes (e.g. 088004012478 -> 88004012478)
  const noZeros = clean.replace(/^0+/, "");
  if (noZeros) variants.add(noZeros);

  // Digits-only padding/stripping for UPC-A, EAN-13, GTIN-14
  if (/^\d+$/.test(clean)) {
    if (clean.length < 12) variants.add(clean.padStart(12, "0"));
    if (clean.length < 13) variants.add(clean.padStart(13, "0"));
    if (clean.length < 14) variants.add(clean.padStart(14, "0"));
    if (clean.length === 13 && clean.startsWith("0")) variants.add(clean.slice(1));
    if (clean.length === 14 && clean.startsWith("00")) variants.add(clean.slice(2));
  }

  return Array.from(variants);
}

export function bottleMatchesBarcode(bottle: ShelfBottle, scannedCode: string): boolean {
  if (!scannedCode) return false;
  const scannedVariants = getBarcodeVariantsList(scannedCode);

  // Check bottle primary UPC
  if (bottle.upc) {
    const bottleUpcVariants = getBarcodeVariantsList(bottle.upc);
    if (scannedVariants.some((sv) => bottleUpcVariants.includes(sv))) return true;
  }

  // Check bottle primary SKU
  if (bottle.sku) {
    const bottleSkuVariants = getBarcodeVariantsList(bottle.sku);
    if (scannedVariants.some((sv) => bottleSkuVariants.includes(sv))) return true;
  }

  // Check all variation UPCs and SKUs
  if (bottle.variations && bottle.variations.length > 0) {
    for (const v of bottle.variations) {
      if (v.upc) {
        const vUpcVariants = getBarcodeVariantsList(v.upc);
        if (scannedVariants.some((sv) => vUpcVariants.includes(sv))) return true;
      }
      if (v.sku) {
        const vSkuVariants = getBarcodeVariantsList(v.sku);
        if (scannedVariants.some((sv) => vSkuVariants.includes(sv))) return true;
      }
    }
  }

  return false;
}

export function getBaseSpiritName(name: string): string {
  return (name || "")
    .replace(/\s*-\s*\d+(\.\d+)?\s*oz$/i, "")
    .replace(/\s*\(\s*\d+(\.\d+)?\s*oz\s*\)$/i, "")
    .replace(/\s*\b(750\s*ml|1000\s*ml|1\s*l|1\.0\s*l|1\s*liter|1\.75\s*l|375\s*ml|50\s*ml|100\s*cl|70\s*cl)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export function isSiblingVariation(bottleName: string, baseName: string): boolean {
  if (!bottleName || !baseName) return false;
  const bBase = getBaseSpiritName(bottleName);
  return bBase === baseName || bBase.startsWith(baseName) || baseName.startsWith(bBase);
}

export function shelfCodeDisplay(code: string): string {
  if (code.startsWith("STOR-")) return "Storage " + code.slice(5);
  if (code.startsWith("LIB-A-")) return "Allocated " + code.slice(6);
  if (code.startsWith("LIB-C-")) return "Cabinet " + code.slice(6);
  if (code.startsWith("LIB-O-")) return "Library Open " + code.slice(6);
  if (code.startsWith("LIB-OPEN-")) return "Library Open " + code.slice(9);
  if (code.startsWith("LIB-")) return "Retail Shelf " + code.slice(4);
  if (code.startsWith("BAR-")) return "Bar Shelf " + code.slice(4);
  if (code.startsWith("RES-")) return "Parlor Back " + code.slice(4);
  if (code.startsWith("SR-")) return "Lounge " + code.slice(3);
  return code;
}

export function getAreaForSlot(slotCode: string): {
  areaId: MainAreaId;
  areaLabel: string;
  shelfLabel: string;
  icon: string;
  zoneId: string;
  prefix: string;
  isStorage: boolean;
  isOpen: boolean;
} {
  const zone = [...SHELF_ZONES]
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find((z) => slotCode.startsWith(z.prefix)) || SHELF_ZONES[0];
  const area = MAIN_AREAS.find((a) => a.zoneIds.includes(zone.id)) || MAIN_AREAS[0];
  return {
    areaId: area.id,
    areaLabel: area.label,
    shelfLabel: zone.label,
    icon: zone.icon,
    zoneId: zone.id,
    prefix: zone.prefix,
    isStorage: zone.prefix === "STOR",
    isOpen: zone.bottleType === "open",
  };
}

export function BarcodeScanIcon({ size = 20, color = "var(--gold, #F9AF36)" }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
    >
      {/* Corner view finder brackets */}
      <path d="M3 7V5a2 2 0 0 1 2-2h2" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" stroke={color} strokeWidth="2" strokeLinecap="round" />

      {/* Barcode lines */}
      <path d="M7 7v10" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M11 7v10" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M14 7v10" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M17 7v10" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function AdminShelfMap() {
  const isPhone = useIsMobile(768);
  const [currentUser, setCurrentUser] = useState<SessionUser | null>(null);
  const isOwner = useMemo(() => {
    return Boolean(currentUser?.bar_roles?.includes("bar_owner"));
  }, [currentUser]);

  const [activeZone, setActiveZone] = useState<string>("SR");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [bottles, setBottles] = useState<ShelfBottle[]>(() => {
    try {
      const cached = localStorage.getItem("station.shelf_bottles_cache");
      if (cached) return JSON.parse(cached);
    } catch {}
    return [];
  });
  const [loading, setLoading] = useState(() => {
    try {
      return !localStorage.getItem("station.shelf_bottles_cache");
    } catch {
      return true;
    }
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);

  // ── Global Spirit & Bottle Locator (Main Shelf Map Search) ──
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [globalSearchTypeFilter, setGlobalSearchTypeFilter] = useState<"all" | "open" | "retail" | "storage" | "unplaced">("all");

  // Selected cell / slot modal
  const [selectedSlotCode, setSelectedSlotCode] = useState<string | null>(null);
  const [slotSearchQuery, setSlotSearchQuery] = useState("");

  // Barcode scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [targetSlotForScan, setTargetSlotForScan] = useState<string | null>(null);

  // ── Bottle Locator & Multi-Location Finder State ──
  const [locatorOpen, setLocatorOpen] = useState(false);
  const [locatorSearchQuery, setLocatorSearchQuery] = useState("");
  const [selectedLocatorBottle, setSelectedLocatorBottle] = useState<ShelfBottle | null>(null);
  const [newLocationSlotInput, setNewLocationSlotInput] = useState("");

  // ── Interactive Bottle Shelf Location Selector Sheet State ──
  const [shelfPickerBottle, setShelfPickerBottle] = useState<ShelfBottle | null>(null);
  const [shelfPickerZoneId, setShelfPickerZoneId] = useState<string>("SR");
  const [shelfPickerCustomInput, setShelfPickerCustomInput] = useState<string>("");

  // ── Shelf Audit State (Lineup Audit) ──
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditSlotCode, setAuditSlotCode] = useState<string | null>(null);
  const [auditScannedBottles, setAuditScannedBottles] = useState<ShelfBottle[]>([]);
  const [auditNotes, setAuditNotes] = useState("");
  const [auditManualQuery, setAuditManualQuery] = useState("");
  const [auditScannerOpen, setAuditScannerOpen] = useState(false);
  const [auditSubmitting, setAuditSubmitting] = useState(false);

  // ── Shelf Audit History State ──
  const [auditHistoryOpen, setAuditHistoryOpen] = useState(false);
  const [auditHistoryRecords, setAuditHistoryRecords] = useState<ShelfAuditRecord[]>([]);
  const [auditHistoryLoading, setAuditHistoryLoading] = useState(false);
  const [historySlotFilter, setHistorySlotFilter] = useState<string | null>(null);

  // ── Smart Relocate / Move Bottle Modal (Owner-Only) ──
  const [smartMoveBottle, setSmartMoveBottle] = useState<ShelfBottle | null>(null);
  const [smartMoveFromSlot, setSmartMoveFromSlot] = useState<string>("");
  const [smartMoveToSlot, setSmartMoveToSlot] = useState<string>("");
  const [smartMoveNotes, setSmartMoveNotes] = useState<string>("");
  const [smartMoveSubmitting, setSmartMoveSubmitting] = useState(false);

  // Add new bottle modal state
  const [addBottleOpen, setAddBottleOpen] = useState(false);
  const [newBottleName, setNewBottleName] = useState("");
  const [newBottleDistillery, setNewBottleDistillery] = useState("");
  const [newBottleCategory, setNewBottleCategory] = useState("Bourbon");
  const [newBottleShelfCode, setNewBottleShelfCode] = useState("");
  const [newBottleUpc, setNewBottleUpc] = useState("");
  const [newBottlePourPrice, setNewBottlePourPrice] = useState("");
  const [newBottlePrice, setNewBottlePrice] = useState("");
  const [syncingSquare, setSyncingSquare] = useState(false);

  // ── Multiple Variations Picker State ──
  const [variationPickerOpen, setVariationPickerOpen] = useState(false);
  const [scannedBarcodeForPicker, setScannedBarcodeForPicker] = useState("");
  const [variationCandidates, setVariationCandidates] = useState<ShelfBottle[]>([]);
  const [variationTargetSlot, setVariationTargetSlot] = useState<string | null>(null);

  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleForceSquareSync = async () => {
    setSyncingSquare(true);
    showToast("🔄 Syncing catalog from Square...");
    try {
      await forceSquareCatalogSync();
      await loadShelvedBottles();
      showToast("✓ Square catalog synced successfully!");
    } catch (err: any) {
      console.error("Square sync failed:", err);
      showToast(`❌ Square sync failed: ${err?.message || "Error"}`, "err");
    } finally {
      setSyncingSquare(false);
    }
  };

  const loadAuditHistory = async (slotCode?: string) => {
    setAuditHistoryLoading(true);
    try {
      const res = await listShelfAuditHistory(slotCode);
      if (res.ok) {
        setAuditHistoryRecords(res.history);
      }
    } catch (err) {
      console.warn("Failed to load shelf audit history:", err);
    } finally {
      setAuditHistoryLoading(false);
    }
  };

  const handleOpenAuditHistory = (slotCode?: string) => {
    setHistorySlotFilter(slotCode || null);
    loadAuditHistory(slotCode);
    setAuditHistoryOpen(true);
  };

  useEffect(() => {
    whoami().then((u) => {
      if (u) setCurrentUser(u);
    });
    loadAuditHistory();
  }, []);

  const loadShelvedBottles = async () => {
    try {
      // 1. Fetch menu_items
      const menuData = await readAllRows<any>((from, to) =>
        supabase
          .from("menu_items")
          .select("id, name, distillery, subcategory, category_slug, shelf_location, bottle_price_cents, pour_price_cents, inventory_count, upc, sku")
          .order("name", { ascending: true })
          .order("id", { ascending: true })
          .range(from, to)
      );

      // 2. Fetch active bottle_variations to index all non-primary sizes and barcodes.
      // Best-effort as before: a failure leaves bottles without their extra sizes.
      const varData = await readAllRows<any>((from, to) =>
        supabase
          .from("bottle_variations")
          .select("id, menu_item_id, size_label, upc, sku, price_cents, is_default, is_active")
          .eq("is_active", true)
          .order("id", { ascending: true })
          .range(from, to)
      ).catch(() => [] as any[]);

      const varByParent = new Map<string, ShelfBottleVariation[]>();
      (varData || []).forEach((v: any) => {
        if (!v.menu_item_id) return;
        const list = varByParent.get(v.menu_item_id) || [];
        list.push({
          id: v.id,
          size_label: v.size_label || "Standard",
          upc: v.upc,
          sku: v.sku,
          bottle_price_cents: v.price_cents,
          is_default: v.is_default,
        });
        varByParent.set(v.menu_item_id, list);
      });

      const pourRegex = POUR_ROW_NAME;
      const physicalMap = new Map<string, ShelfBottle>();
      const pourItems: ShelfBottle[] = [];

      (menuData || []).forEach((p: any) => {
        let locs: string[] = [];
        if (Array.isArray(p.shelf_location)) {
          locs = p.shelf_location.filter(Boolean);
        } else if (typeof p.shelf_location === "string" && p.shelf_location.trim()) {
          locs = [p.shelf_location.trim()];
        }

        const b: ShelfBottle = {
          id: p.id,
          name: p.name,
          distillery: p.distillery,
          category: p.subcategory || p.category_slug || "Spirit",
          shelf_location: locs,
          retail_price_cents: p.bottle_price_cents,
          pour_price_cents: p.pour_price_cents,
          inventory_count: typeof p.inventory_count === "number" ? p.inventory_count : null,
          upc: p.upc,
          sku: p.sku,
          variations: varByParent.get(p.id) || [],
        };

        if (pourRegex.test(p.name?.trim() || "")) {
          pourItems.push(b);
        } else {
          physicalMap.set((p.name?.trim() || "").toLowerCase(), b);
        }
      });

      // "- N oz" rows are Square order options for a pour, never retail bottles, so
      // each folds into its same-named bottle. (menu_items.bottle_id is the true link
      // but the browser role cannot select it: 42501.)
      pourItems.forEach((pour) => {
        const baseKey = (pour.name || "").trim().replace(pourRegex, "").trim().toLowerCase();
        const existing = physicalMap.get(baseKey);
        if (existing) {
          if (!existing.pour_price_cents && pour.pour_price_cents) {
            existing.pour_price_cents = pour.pour_price_cents;
          }
          if (!existing.upc && pour.upc) {
            existing.upc = pour.upc;
          }
          if (!existing.sku && pour.sku) {
            existing.sku = pour.sku;
          }
          // Merge any shelf location assigned to the pour record
          pour.shelf_location.forEach((loc) => {
            if (!existing.shelf_location.includes(loc)) {
              existing.shelf_location.push(loc);
            }
          });
          // Keep barcodes on the pour's size variations scannable via the bottle
          (pour.variations || []).forEach((v) => {
            if (!existing.variations?.some((ev) => ev.id === v.id)) {
              existing.variations = [...(existing.variations || []), v];
            }
          });
        } else if (pour.shelf_location.length > 0) {
          // No same-named bottle, but the pour row holds a real placement: keep it
          // under the bottle name rather than empty the slot. Unplaced ones drop.
          physicalMap.set(baseKey, {
            ...pour,
            name: (pour.name || "").trim().replace(pourRegex, "").trim(),
          });
        }
      });

      const mapped = Array.from(physicalMap.values()).sort((a, b) => a.name.localeCompare(b.name));

      setBottles(mapped);
      try {
        localStorage.setItem("station.shelf_bottles_cache", JSON.stringify(mapped));
      } catch {}
    } catch (err: any) {
      console.error("Failed to load shelf maps:", err);
      showToast("Error loading bottle locations.", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShelvedBottles();
  }, []);

  const activeZoneObj = useMemo(() => {
    return SHELF_ZONES.find((z) => z.id === activeZone) || SHELF_ZONES[0];
  }, [activeZone]);

  const activeAreaObj = useMemo(() => {
    return MAIN_AREAS.find((a) => a.zoneIds.includes(activeZoneObj.id)) || MAIN_AREAS[0];
  }, [activeZoneObj]);

  const roomShelves = useMemo(() => {
    return SHELF_ZONES.filter((z) => z.areaId === activeAreaObj.id);
  }, [activeAreaObj]);

  // Map of slotCode -> Array of bottles placed in that slot
  const locationMap = useMemo(() => {
    const map: Record<string, ShelfBottle[]> = {};
    for (const b of bottles) {
      for (const loc of b.shelf_location) {
        if (!map[loc]) map[loc] = [];
        map[loc].push(b);
      }
    }
    return map;
  }, [bottles]);

  // Zone statistics
  const zoneStats = useMemo(() => {
    const totalSlots = activeZoneObj.cols * activeZoneObj.rows.length;
    let occupied = 0;
    let totalPlacedBottles = 0;

    for (let c = 1; c <= activeZoneObj.cols; c++) {
      for (let r = 0; r < activeZoneObj.rows.length; r++) {
        const code = `${activeZoneObj.prefix}-${c}${activeZoneObj.rows[r]}`;
        const items = locationMap[code] || [];
        if (items.length > 0) {
          occupied++;
          totalPlacedBottles += items.length;
        }
      }
    }

    return {
      totalSlots,
      occupied,
      empty: totalSlots - occupied,
      totalPlacedBottles,
    };
  }, [activeZoneObj, locationMap]);

  // Global Retail vs Open Counts vs Back Stock
  const globalCounts = useMemo(() => {
    let openCount = 0;
    let retailCount = 0;
    let backStockCount = 0;
    for (const b of bottles) {
      if (b.pour_price_cents && b.pour_price_cents > 0) openCount++;
      if (b.retail_price_cents && b.retail_price_cents > 0) retailCount++;
      if ((b.inventory_count && b.inventory_count > 1) || b.shelf_location.some((loc) => loc.startsWith("STOR-"))) {
        backStockCount++;
      }
    }
    return { openCount, retailCount, backStockCount, total: bottles.length };
  }, [bottles]);

  // Filtered bottles for table/list search with category and retail/open/back-stock support
  const filteredBottles = useMemo(() => {
    let list = bottles;

    // Inventory Type filter (Retail vs Open Pour vs Back Stock)
    if (globalSearchTypeFilter === "open") {
      list = list.filter((b) => Boolean(b.pour_price_cents && b.pour_price_cents > 0));
    } else if (globalSearchTypeFilter === "retail") {
      list = list.filter((b) => Boolean(b.retail_price_cents && b.retail_price_cents > 0));
    } else if (globalSearchTypeFilter === "storage") {
      list = list.filter((b) => (b.inventory_count && b.inventory_count > 1) || b.shelf_location.some((loc) => loc.startsWith("STOR-")));
    } else if (globalSearchTypeFilter === "unplaced") {
      list = list.filter((b) => b.shelf_location.length === 0);
    }

    // Category filter
    if (categoryFilter === "empty") {
      list = list.filter((b) => b.shelf_location.length === 0);
    } else if (categoryFilter === "bourbon") {
      list = list.filter((b) => (b.category?.toLowerCase() || "").includes("bourbon") || b.name.toLowerCase().includes("bourbon"));
    } else if (categoryFilter === "rye") {
      list = list.filter((b) => (b.category?.toLowerCase() || "").includes("rye") || b.name.toLowerCase().includes("rye"));
    } else if (categoryFilter === "single_malt") {
      list = list.filter((b) => (b.category?.toLowerCase() || "").includes("malt") || b.name.toLowerCase().includes("malt"));
    } else if (categoryFilter === "scotch") {
      list = list.filter((b) => (b.category?.toLowerCase() || "").includes("scotch") || b.name.toLowerCase().includes("scotch"));
    } else if (categoryFilter === "agave") {
      list = list.filter((b) => (b.category?.toLowerCase() || "").includes("tequila") || (b.category?.toLowerCase() || "").includes("mezcal") || b.name.toLowerCase().includes("tequila"));
    }

    if (!globalSearchQuery.trim()) {
      return list.filter((b) =>
        categoryFilter === "empty" ? b.shelf_location.length === 0 : b.shelf_location.some((loc) => loc.startsWith(activeZoneObj.prefix))
      );
    }
    const q = globalSearchQuery.toLowerCase();
    return list.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        (b.distillery && b.distillery.toLowerCase().includes(q)) ||
        (b.category && b.category.toLowerCase().includes(q)) ||
        b.shelf_location.some((loc) => loc.toLowerCase().includes(q)) ||
        bottleMatchesBarcode(b, q)
    );
  }, [bottles, globalSearchQuery, activeZoneObj, categoryFilter, globalSearchTypeFilter]);

  // Helper to sort catalog matches prioritizing exact matches and sealed retail items
  const sortMatches = (list: ShelfBottle[], query: string) => {
    const q = query.toLowerCase().trim();
    return [...list].sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aExact = aName === q;
      const bExact = bName === q;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      const aStarts = aName.startsWith(q);
      const bStarts = bName.startsWith(q);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      // Has UPC barcode match first
      const aUpc = bottleMatchesBarcode(a, q);
      const bUpc = bottleMatchesBarcode(b, q);
      if (aUpc && !bUpc) return -1;
      if (!aUpc && bUpc) return 1;

      // Has Retail price
      const aRetail = Boolean(a.retail_price_cents && a.retail_price_cents > 0);
      const bRetail = Boolean(b.retail_price_cents && b.retail_price_cents > 0);
      if (aRetail && !bRetail) return -1;
      if (!aRetail && bRetail) return 1;

      return a.name.localeCompare(b.name);
    });
  };

  // ── Global Search Matches across all Inventory (Open & Closed) ──
  const globalSearchMatches = useMemo(() => {
    if (!globalSearchQuery.trim()) return [];
    const q = globalSearchQuery.toLowerCase().trim();

    let list = bottles.filter((b) => {
      const nameMatch = b.name.toLowerCase().includes(q);
      const distilleryMatch = Boolean(b.distillery && b.distillery.toLowerCase().includes(q));
      const catMatch = Boolean(b.category && b.category.toLowerCase().includes(q));
      const upcMatch = bottleMatchesBarcode(b, q);
      const locMatch = b.shelf_location.some((loc) => loc.toLowerCase().includes(q));
      return nameMatch || distilleryMatch || catMatch || upcMatch || locMatch;
    });

    if (globalSearchTypeFilter === "open") {
      list = list.filter((b) => Boolean(b.pour_price_cents && b.pour_price_cents > 0) || b.shelf_location.some((l) => l.startsWith("SR-")));
    } else if (globalSearchTypeFilter === "retail") {
      list = list.filter((b) => Boolean(b.retail_price_cents && b.retail_price_cents > 0) || b.shelf_location.some((l) => l.startsWith("LIB-") || l.startsWith("RES-") || l.startsWith("BAR-")));
    } else if (globalSearchTypeFilter === "storage") {
      list = list.filter((b) => b.shelf_location.some((l) => l.startsWith("STOR-")) || (b.inventory_count && b.inventory_count > 1));
    } else if (globalSearchTypeFilter === "unplaced") {
      list = list.filter((b) => b.shelf_location.length === 0);
    }

    return sortMatches(list, globalSearchQuery).slice(0, 32);
  }, [bottles, globalSearchQuery, globalSearchTypeFilter]);

  // Set of bottle IDs matching global search for fast O(1) grid lookup
  const globalSearchMatchIds = useMemo(() => {
    if (!globalSearchQuery.trim()) return new Set<string>();
    return new Set(globalSearchMatches.map((b) => b.id));
  }, [globalSearchMatches, globalSearchQuery]);

  const unplacedBottlesCount = useMemo(() => {
    return bottles.filter((b) => b.shelf_location.length === 0).length;
  }, [bottles]);

  // Search results inside Slot Assignment Sheet
  const slotCatalogMatches = useMemo(() => {
    if (!slotSearchQuery.trim() || slotSearchQuery.length < 2) return [];
    const q = slotSearchQuery.toLowerCase();
    const filtered = bottles.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        (b.distillery && b.distillery.toLowerCase().includes(q)) ||
        (b.category && b.category.toLowerCase().includes(q)) ||
        bottleMatchesBarcode(b, q)
    );
    return sortMatches(filtered, slotSearchQuery).slice(0, 18);
  }, [bottles, slotSearchQuery]);

  // Search results inside Locator Modal
  const locatorMatches = useMemo(() => {
    if (!locatorSearchQuery.trim() || locatorSearchQuery.length < 2) return [];
    const q = locatorSearchQuery.toLowerCase();
    const filtered = bottles.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        (b.distillery && b.distillery.toLowerCase().includes(q)) ||
        (b.category && b.category.toLowerCase().includes(q)) ||
        (b.upc && b.upc.includes(q)) ||
        (b.sku && b.sku.toLowerCase().includes(q)) ||
        b.shelf_location.some((loc) => loc.toLowerCase().includes(q))
    );
    return sortMatches(filtered, locatorSearchQuery).slice(0, 24);
  }, [bottles, locatorSearchQuery]);

  // Related bottles/variants in venue for currently selected locator bottle
  const relatedLocatorBottles = useMemo(() => {
    if (!selectedLocatorBottle) return [];
    const targetName = selectedLocatorBottle.name.toLowerCase().replace(/ - 1 oz$/i, "").replace(/ \(retail\)$/i, "").trim();
    return bottles.filter((b) => {
      if (b.id === selectedLocatorBottle.id) return false;
      const otherName = b.name.toLowerCase().replace(/ - 1 oz$/i, "").replace(/ \(retail\)$/i, "").trim();
      const sameUpc = Boolean(b.upc && selectedLocatorBottle.upc && b.upc === selectedLocatorBottle.upc);
      const sameBaseName = otherName.length >= 4 && (otherName.includes(targetName) || targetName.includes(otherName));
      return sameUpc || sameBaseName;
    });
  }, [bottles, selectedLocatorBottle]);

  // Search results inside Shelf Audit Modal
  const auditCatalogMatches = useMemo(() => {
    if (!auditManualQuery.trim() || auditManualQuery.length < 2) return [];
    const q = auditManualQuery.toLowerCase();
    const filtered = bottles.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        (b.distillery && b.distillery.toLowerCase().includes(q)) ||
        (b.category && b.category.toLowerCase().includes(q)) ||
        (b.upc && b.upc.includes(q)) ||
        (b.sku && b.sku.toLowerCase().includes(q))
    );
    return sortMatches(filtered, auditManualQuery).slice(0, 15);
  }, [bottles, auditManualQuery]);

  // ── Actions: Assign & Remove ──
  const handleAssignToSlot = async (bottleId: string, slotCode: string) => {
    const bottle = bottles.find((b) => b.id === bottleId);
    if (!bottle) return;

    if (bottle.shelf_location.includes(slotCode)) {
      showToast(`"${bottle.name}" is already placed in ${slotCode}`);
      return;
    }

    const updated = [...bottle.shelf_location, slotCode];
    setSaving(true);

    try {
      await updateBottleShelfLocation(bottleId, updated);

      setBottles((prev) => {
        const next = prev.map((b) => (b.id === bottleId ? { ...b, shelf_location: updated } : b));
        try {
          localStorage.setItem("station.shelf_bottles_cache", JSON.stringify(next));
        } catch {}
        return next;
      });
      showToast(`✓ Assigned "${bottle.name}" to ${slotCode}`);
      setSlotSearchQuery("");
      if (selectedLocatorBottle?.id === bottleId) {
        setSelectedLocatorBottle((prev) => (prev ? { ...prev, shelf_location: updated } : null));
      }
      if (shelfPickerBottle?.id === bottleId) {
        setShelfPickerBottle((prev) => (prev ? { ...prev, shelf_location: updated } : null));
      }
    } catch (err: any) {
      console.error("Assign error:", err);
      showToast(`❌ Failed to assign shelf slot: ${err?.message || "Database update rejected"}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFromSlot = async (bottleId: string, slotCode: string) => {
    const bottle = bottles.find((b) => b.id === bottleId);
    if (!bottle) return;

    const updated = bottle.shelf_location.filter((loc) => loc !== slotCode);
    setSaving(true);

    try {
      await updateBottleShelfLocation(bottleId, updated);

      setBottles((prev) => {
        const next = prev.map((b) => (b.id === bottleId ? { ...b, shelf_location: updated } : b));
        try {
          localStorage.setItem("station.shelf_bottles_cache", JSON.stringify(next));
        } catch {}
        return next;
      });
      showToast(`Removed "${bottle.name}" from ${slotCode}`);
      if (selectedLocatorBottle?.id === bottleId) {
        setSelectedLocatorBottle((prev) => (prev ? { ...prev, shelf_location: updated } : null));
      }
      if (shelfPickerBottle?.id === bottleId) {
        setShelfPickerBottle((prev) => (prev ? { ...prev, shelf_location: updated } : null));
      }
    } catch (err: any) {
      console.error("Remove error:", err);
      showToast(`❌ Failed to remove shelf slot: ${err?.message || "Database update rejected"}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Jump to Slot on Map ──
  const handleJumpToSlot = (slotCode: string) => {
    const matchedZone = [...SHELF_ZONES]
      .sort((a, b) => b.prefix.length - a.prefix.length)
      .find((z) => slotCode.startsWith(z.prefix)) || SHELF_ZONES[0];
    setActiveZone(matchedZone.id);
    setSelectedSlotCode(slotCode);
    setLocatorOpen(false);
  };

  // ── Barcode Scanning ──
  const handleScanBarcode = async (code: string) => {
    setScannerOpen(false);
    const cleanedCode = code.trim();
    if (!cleanedCode) return;

    showToast(`Scanning barcode: ${cleanedCode}...`);

    // 1. Find exact matching bottles in local memory using all barcode variants
    let matchedBottles = bottles.filter((b) => bottleMatchesBarcode(b, cleanedCode));

    // 2. If no local matches, query remote Edge Function exact resolver
    if (matchedBottles.length === 0) {
      try {
        const res = await lookupBottleByBarcode(cleanedCode);
        const bottleMatches = (res?.matches || []).filter((item: any) => !isPourMatch(item));
        if (bottleMatches.length > 0) {
          const remoteFoundList: ShelfBottle[] = bottleMatches.map((item: any) => {
            const m = item.bottle || item;
            let locs: string[] = [];
            if (Array.isArray(m.shelf_location)) {
              locs = m.shelf_location.filter(Boolean);
            } else if (typeof m.shelf_location === "string" && m.shelf_location.trim()) {
              locs = [m.shelf_location.trim()];
            }

            const sizeBadge = item.variation?.size_label ? ` (${item.variation.size_label})` : "";
            const displayName = `${m.name.replace(/\s*-\s*\d+(\.\d+)?\s*oz$/i, "").trim()}${sizeBadge}`;

            return {
              id: m.id,
              name: displayName,
              distillery: m.distillery,
              category: m.subcategory || m.category_slug || "Spirit",
              shelf_location: locs,
              retail_price_cents: item.variation?.bottle_price_cents || m.bottle_price_cents,
              pour_price_cents: m.pour_price_cents,
              inventory_count: typeof m.inventory_count === "number" ? m.inventory_count : null,
              upc: item.variation?.upc || m.upc || cleanedCode,
              sku: item.variation?.sku || m.sku,
            };
          });

          matchedBottles = remoteFoundList;
          setBottles((prev) => {
            let next = [...prev];
            for (const rem of remoteFoundList) {
              const idx = next.findIndex((b) => b.id === rem.id);
              if (idx >= 0) {
                next[idx] = {
                  ...next[idx],
                  upc: next[idx].upc || rem.upc,
                  shelf_location: Array.from(new Set([...next[idx].shelf_location, ...rem.shelf_location])),
                };
              } else {
                next = [rem, ...next];
              }
            }
            return next;
          });
        }
      } catch (err) {
        // A failed lookup is not "not found" — falling through would offer to
        // create a duplicate bottle for a barcode that may already exist.
        console.warn("Remote barcode lookup failed:", err);
        setTargetSlotForScan(null);
        showToast(`Barcode lookup failed: ${(err as Error)?.message || "unknown error"}. Try again.`, "err");
        return;
      }
    }

    // ── 3. If MULTIPLE ambiguous matches found: Open Variation Selection Modal! ──
    if (matchedBottles.length > 1) {
      const destSlot = targetSlotForScan;
      setTargetSlotForScan(null);
      setVariationTargetSlot(destSlot);
      setScannedBarcodeForPicker(cleanedCode);
      setVariationCandidates(matchedBottles);
      setVariationPickerOpen(true);
      return;
    }

    // ── 4. If EXACTLY 1 bottle found: ──
    if (matchedBottles.length === 1) {
      const found = matchedBottles[0];
      if (targetSlotForScan) {
        const destSlot = targetSlotForScan;
        setTargetSlotForScan(null);
        await handleAssignToSlot(found.id, destSlot);
        return;
      }

      // Global Search Barcode Scan: Search & Locate Physical Placements & Variations
      const cleanName = found.name.replace(/\s*-\s*\d+(\.\d+)?\s*oz$/i, "").trim();
      setGlobalSearchQuery(cleanName);

      const varCount = found.variations?.length || 1;
      const varNote = varCount > 1 ? ` · ${varCount} sizes available` : "";

      if (found.shelf_location.length > 0) {
        const firstSlot = found.shelf_location[0];
        const matchedZone = [...SHELF_ZONES]
          .sort((a, b) => b.prefix.length - a.prefix.length)
          .find((z) => firstSlot.startsWith(z.prefix)) || SHELF_ZONES[0];
        setActiveZone(matchedZone.id);
        setSelectedSlotCode(firstSlot);
        showToast(
          `✓ Located "${cleanName}" (${found.shelf_location.map(shelfCodeDisplay).join(", ")})${varNote}`
        );
      } else {
        showToast(`✓ Located "${cleanName}" (Unplaced on shelves)${varNote}`);
      }
      return;
    }

    // ── 5. 0 matches found: ──
    if (targetSlotForScan) {
      const destSlot = targetSlotForScan;
      setTargetSlotForScan(null);
      handleOpenAddBottle(cleanedCode, destSlot);
      return;
    }

    setGlobalSearchQuery(cleanedCode);
    showToast(`Barcode "${cleanedCode}" not recognized in catalog. Tap "+ Add Custom Spirit" to create.`);
  };

  // ── Shelf Lineup Audit Handlers ──
  const handleOpenAudit = (slotCode?: string) => {
    const targetSlot = slotCode || selectedSlotCode || `${activeZoneObj.prefix}-1A`;
    setAuditSlotCode(targetSlot);
    const existing = locationMap[targetSlot] || [];
    setAuditScannedBottles([...existing]); // Pre-populate with existing slot lineup for intuitive verification
    setAuditNotes("");
    setAuditManualQuery("");
    setAuditOpen(true);
  };

  const handleScanAuditBarcode = async (barcode: string) => {
    const code = barcode.trim();
    if (!code) return;

    // Look up locally first with robust barcode variants
    let match = bottles.find((b) => bottleMatchesBarcode(b, code));

    if (!match) {
      // Query remote Edge Function exact resolver
      try {
        const res = await lookupBottleByBarcode(code);
        const bottleMatches = (res?.matches || []).filter((item: any) => !isPourMatch(item));
        if (bottleMatches.length > 0) {
          const matchItem = bottleMatches[0];
          const m = matchItem.bottle || matchItem;
          let locs: string[] = [];
          if (Array.isArray(m.shelf_location)) {
            locs = m.shelf_location.filter(Boolean);
          } else if (typeof m.shelf_location === "string" && m.shelf_location.trim()) {
            locs = [m.shelf_location.trim()];
          }

          const sizeBadge = matchItem.variation?.size_label ? ` (${matchItem.variation.size_label})` : "";
          const displayName = `${m.name.replace(/\s*-\s*\d+(\.\d+)?\s*oz$/i, "").trim()}${sizeBadge}`;

          match = {
            id: m.id,
            name: displayName,
            distillery: m.distillery,
            category: m.subcategory || m.category_slug || "Spirit",
            shelf_location: locs,
            retail_price_cents: matchItem.variation?.bottle_price_cents || m.bottle_price_cents,
            pour_price_cents: m.pour_price_cents,
            inventory_count: typeof m.inventory_count === "number" ? m.inventory_count : null,
            upc: matchItem.variation?.upc || m.upc || code,
            sku: matchItem.variation?.sku || m.sku,
          };

          setBottles((prev) => (prev.some((b) => b.id === match!.id) ? prev : [match!, ...prev]));
        }
      } catch (err) {
        console.warn("Audit remote barcode lookup failed:", err);
        showToast(`Barcode lookup failed: ${(err as Error)?.message || "unknown error"}. Try again.`, "err");
        return;
      }
    }

    if (match) {
      if (auditScannedBottles.some((b) => b.id === match!.id)) {
        showToast(`"${match.name.replace(/ - 1 oz$/i, "")}" is already in this shelf lineup.`);
      } else {
        setAuditScannedBottles((prev) => [match!, ...prev]);
        showToast(`✓ Added: "${match.name.replace(/ - 1 oz$/i, "")}"`);
      }
    } else {
      showToast(`Barcode "${code}" not found. Enter name to search.`);
    }
  };

  const handleApplyAudit = async () => {
    if (!auditSlotCode) return;
    const targetSlot = auditSlotCode;
    const expected = locationMap[targetSlot] || [];
    const scanned = auditScannedBottles;

    // Missing bottles: currently in this slot, but not in audited lineup (marked empty / removed)
    const missing = expected.filter((b) => !scanned.some((s) => s.id === b.id));
    // Added bottles: in audited lineup, but not currently in this slot
    const added = scanned.filter((s) => !expected.some((e) => e.id === s.id));
    // Confirmed: both expected and scanned
    const confirmed = expected.filter((e) => scanned.some((s) => s.id === e.id));

    setAuditSubmitting(true);

    try {
      const res = await batchAuditShelfSlot({
        slot_code: targetSlot,
        scanned_bottle_ids: scanned.map((b) => b.id),
        missing_bottle_ids: missing.map((b) => b.id),
        added_bottle_ids: added.map((b) => b.id),
        notes: auditNotes.trim() || null,
      });

      // Update local state
      setBottles((prev) => {
        const next = prev.map((b) => {
          if (missing.some((m) => m.id === b.id)) {
            const locs = b.shelf_location.filter((loc) => loc !== targetSlot);
            return { ...b, shelf_location: locs };
          }
          if (added.some((a) => a.id === b.id)) {
            const locs = Array.from(new Set([...b.shelf_location, targetSlot]));
            return { ...b, shelf_location: locs };
          }
          return b;
        });

        try {
          localStorage.setItem("station.shelf_bottles_cache", JSON.stringify(next));
        } catch {}
        return next;
      });

      if (res.audit_entry) {
        setAuditHistoryRecords((prev) => [res.audit_entry, ...prev]);
      }

      showToast(
        `✓ Shelf ${targetSlot} audit saved: ${scanned.length} spirits placed (${confirmed.length} verified, ${added.length} added, ${missing.length} unassigned).`
      );
      setAuditOpen(false);
    } catch (err: any) {
      console.error("Failed to apply shelf lineup audit:", err);
      showToast(`❌ Failed to save audit: ${err?.message || "Database update failed"}`, "err");
    } finally {
      setAuditSubmitting(false);
    }
  };

  // ── Smart Relocate / Move Bottle Handler (Owner-Only) ──
  const handleExecuteSmartMove = async () => {
    if (!smartMoveBottle || !smartMoveFromSlot || !smartMoveToSlot.trim()) {
      showToast("Please specify a destination shelf slot", "err");
      return;
    }
    const bId = smartMoveBottle.id;
    const dest = smartMoveToSlot.trim().toUpperCase();
    const from = smartMoveFromSlot;

    setSmartMoveSubmitting(true);
    try {
      const res = await smartMoveShelfBottle({
        bottle_id: bId,
        from_slot: from,
        to_slot: dest,
        notes: smartMoveNotes.trim() || null,
      });

      // Update local state
      setBottles((prev) => {
        const next = prev.map((b) => {
          if (b.id === bId) {
            const nextLocs = Array.from(new Set([...b.shelf_location.filter((l) => l !== from), dest]));
            return { ...b, shelf_location: nextLocs };
          }
          return b;
        });
        try {
          localStorage.setItem("station.shelf_bottles_cache", JSON.stringify(next));
        } catch {}
        return next;
      });

      if (res.move) {
        setAuditHistoryRecords((prev) => [res.move, ...prev]);
      }

      showToast(`✓ Relocated "${smartMoveBottle.name}" from ${from} to ${dest} (Logged in audit history)`);
      setSmartMoveBottle(null);
      if (selectedSlotCode === from) {
        setSelectedSlotCode(null);
      }
    } catch (err: any) {
      console.error("Smart move error:", err);
      showToast(`❌ Relocation failed: ${err?.message || "Database error"}`, "err");
    } finally {
      setSmartMoveSubmitting(false);
    }
  };

  const handleOpenAddBottle = (initialUpc: string = "", initialSlot: string = "", initialName: string = "") => {
    setNewBottleName(initialName);
    setNewBottleDistillery("");
    setNewBottleCategory("Bourbon");
    setNewBottleShelfCode(initialSlot || `${activeZoneObj.prefix}-1A`);
    setNewBottleUpc(initialUpc);
    setNewBottlePourPrice("");
    setNewBottlePrice("");
    setAddBottleOpen(true);
  };

  const handleSaveNewBottle = async () => {
    if (!newBottleName.trim()) {
      showToast("Bottle name is required", "err");
      return;
    }
    setSaving(true);
    const trimmedShelf = newBottleShelfCode.trim() || `${activeZoneObj.prefix}-1A`;
    const pourPriceCents = Math.round((parseFloat(newBottlePourPrice) || 0) * 100);
    const bottlePriceCents = Math.round((parseFloat(newBottlePrice) || 0) * 100);

    try {
      const data = await insertCustomMenuItem({
        name: newBottleName.trim(),
        distillery: newBottleDistillery.trim() || null,
        category_slug: newBottleCategory.toLowerCase().replace(/\s+/g, "_"),
        subcategory: newBottleCategory.trim() || null,
        shelf_location: [trimmedShelf],
        upc: newBottleUpc.trim() || null,
        pour_price_cents: pourPriceCents > 0 ? pourPriceCents : null,
        bottle_price_cents: bottlePriceCents > 0 ? bottlePriceCents : null,
        is_active: true,
      });

      const created: ShelfBottle = {
        id: data.id,
        name: data.name,
        distillery: data.distillery,
        category: data.subcategory || data.category_slug || "Spirit",
        shelf_location: [trimmedShelf],
        retail_price_cents: data.bottle_price_cents,
        pour_price_cents: data.pour_price_cents,
        inventory_count: typeof data.inventory_count === "number" ? data.inventory_count : null,
        upc: data.upc,
      };

      setBottles((prev) => [created, ...prev]);
      setAddBottleOpen(false);
      showToast(`✓ Added "${created.name}" to ${trimmedShelf}`);
    } catch (err: any) {
      console.error("Add error:", err);
      showToast("Error adding bottle: " + (err.message || "Failed"), "err");
    } finally {
      setSaving(false);
    }
  };

  // ── Render 2D Grid ──
  const renderPhysicalGrid = () => {
    const zone = activeZoneObj;
    const gaps = zone.gapsAfter || [];
    const customGaps = zone.columnGaps || {};
    
    // Grid template: 44px row label + 1fr per column + gaps
    const gridCols = [];
    gridCols.push("44px");
    for (let c = 1; c <= zone.cols; c++) {
      gridCols.push("minmax(140px, 1fr)");
      if (gaps.includes(c)) {
        const gapPx = customGaps[c] ? `${customGaps[c]}px` : "16px";
        gridCols.push(gapPx);
      }
    }

    // Rows top-to-bottom: highest letter first (e.g. E -> D -> C -> B -> A, or I -> ... -> A)
    const reversedRows = zone.rows.split("").reverse();

    return (
      <div style={{ overflowX: "auto", paddingBottom: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridCols.join(" "),
            gap: "8px 8px",
            minWidth: zone.cols * 150 + 60,
          }}
        >
          {/* Column Header Row */}
          <div style={styles.gridHeaderCell} />
          {Array.from({ length: zone.cols }, (_, i) => i + 1).map((colNum) => (
            <div key={`col-hdr-${colNum}`} style={{ display: "contents" }}>
              <div style={styles.gridColHeader}>
                Col {colNum}
              </div>
              {gaps.includes(colNum) && (
                <div
                  style={{
                    ...styles.gridAisleGap,
                    width: customGaps[colNum] ? customGaps[colNum] : 14,
                  }}
                  title={zone.id === "STOR" && colNum === 1 ? "Main Storage Aisle" : "Room Aisle Split"}
                />
              )}
            </div>
          ))}

          {/* Grid Rows */}
          {reversedRows.map((rowLetter) => (
            <div key={`row-${rowLetter}`} style={{ display: "contents" }}>
              {/* Row Label (E, D, C, B, A) */}
              <div style={styles.gridRowLabel}>
                <span style={styles.rowLetterBadge}>{rowLetter}</span>
              </div>

              {/* Cells in this Row */}
              {Array.from({ length: zone.cols }, (_, i) => i + 1).map((colNum) => {
                const slotCode = `${zone.prefix}-${colNum}${rowLetter}`;
                const itemsInSlot = locationMap[slotCode] || [];
                const isOccupied = itemsInSlot.length > 0;
                const isSelected = selectedSlotCode === slotCode;

                const hasGlobalSearch = Boolean(globalSearchQuery.trim());
                const slotHasSearchMatch = hasGlobalSearch && itemsInSlot.some((b) => globalSearchMatchIds.has(b.id));

                // Category & Inventory Type matching
                const hasMatch = (categoryFilter === "all" && globalSearchTypeFilter === "all") || (
                  categoryFilter === "empty" ? !isOccupied : itemsInSlot.some((b) => {
                    // Inventory Type check
                    if (globalSearchTypeFilter === "open" && (!b.pour_price_cents || b.pour_price_cents <= 0)) return false;
                    if (globalSearchTypeFilter === "retail" && (!b.retail_price_cents || b.retail_price_cents <= 0)) return false;
                    if (globalSearchTypeFilter === "storage" && ((!b.inventory_count || b.inventory_count <= 1) && !b.shelf_location.some((loc) => loc.startsWith("STOR-")))) return false;
                    if (globalSearchTypeFilter === "unplaced") return false;

                    // Category check
                    if (categoryFilter === "bourbon") return (b.category?.toLowerCase() || "").includes("bourbon") || b.name.toLowerCase().includes("bourbon");
                    if (categoryFilter === "rye") return (b.category?.toLowerCase() || "").includes("rye") || b.name.toLowerCase().includes("rye");
                    if (categoryFilter === "single_malt") return (b.category?.toLowerCase() || "").includes("malt") || b.name.toLowerCase().includes("malt");
                    if (categoryFilter === "scotch") return (b.category?.toLowerCase() || "").includes("scotch") || b.name.toLowerCase().includes("scotch");
                    if (categoryFilter === "agave") return (b.category?.toLowerCase() || "").includes("tequila") || (b.category?.toLowerCase() || "").includes("mezcal") || b.name.toLowerCase().includes("tequila");
                    return true;
                  })
                );

                const isHighlighted = slotHasSearchMatch || (((categoryFilter !== "all" || globalSearchTypeFilter !== "all") && hasMatch && isOccupied));
                const cellOpacity = hasGlobalSearch ? (slotHasSearchMatch ? 1 : 0.22) : (hasMatch ? 1 : 0.3);

                return (
                  <div key={slotCode} style={{ display: "contents" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSlotCode(slotCode);
                        setSlotSearchQuery("");
                      }}
                      style={{
                        ...styles.gridSlotCell,
                        ...(isOccupied ? styles.gridSlotOccupied : styles.gridSlotEmpty),
                        ...(isSelected ? styles.gridSlotSelected : {}),
                        opacity: cellOpacity,
                        borderColor: slotHasSearchMatch ? "var(--gold, #F9AF36)" : (isHighlighted ? "var(--gold, #F9AF36)" : undefined),
                        boxShadow: slotHasSearchMatch ? "0 0 16px rgba(249,175,54,0.55)" : (isHighlighted ? "0 0 10px rgba(249,175,54,0.3)" : undefined),
                      }}
                    >
                      <div style={styles.slotHeaderRow}>
                        <span style={styles.slotCodeText}>{shelfCodeDisplay(slotCode)}</span>
                        {slotHasSearchMatch ? (
                          <span style={styles.slotMatchBadge}>✨ MATCH</span>
                        ) : isOccupied ? (
                          <span style={styles.slotCountBadge}>
                            {itemsInSlot.length} spirit{itemsInSlot.length > 1 ? "s" : ""}
                          </span>
                        ) : null}
                      </div>

                      {isOccupied ? (
                        <div style={styles.slotPillList}>
                          {itemsInSlot.slice(0, 3).map((b) => {
                            const hasBackStock = (b.inventory_count && b.inventory_count > 1) || b.shelf_location.some((l) => l.startsWith("STOR-"));
                            return (
                              <div key={b.id} style={styles.slotBottlePill} title={b.name}>
                                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {b.name.replace(/ - 1 oz$/i, "")}
                                </div>
                                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                  {hasBackStock && (
                                    <span style={styles.miniBackStockTag} title="Back stock available in back storage room">
                                      📦
                                    </span>
                                  )}
                                  {b.pour_price_cents ? (
                                    <span style={styles.miniPourTag}>🟢 ${(b.pour_price_cents / 100).toFixed(0)}</span>
                                  ) : b.retail_price_cents ? (
                                    <span style={styles.miniRetailTag}>🏷️ ${(b.retail_price_cents / 100).toFixed(0)}</span>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                          {itemsInSlot.length > 3 && (
                            <div style={styles.slotMoreBadge}>
                              +{itemsInSlot.length - 3} more spirits
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={styles.slotEmptyHint}>
                          {activeZoneObj.bottleType === "open" ? "🟢 + Place Open Bottle" : "+ Place Sealed Bottle"}
                        </div>
                      )}
                    </button>

                    {gaps.includes(colNum) && (
                      <div
                        style={{
                          ...styles.gridAisleGap,
                          width: customGaps[colNum] ? customGaps[colNum] : 14,
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <RecipeStudioTabs
        activeTab="shelfMap"
        badgeCounts={{ shelfMap: bottles.filter((b) => b.shelf_location.length > 0).length }}
      />

      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* ═══ GLOBAL BOTTLE SEARCH & MULTI-LOCATION FINDER (Open & Closed) ═══ */}
      <div style={styles.globalSearchCard}>
        <div style={styles.globalSearchHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <h2 style={styles.globalSearchTitle}>
              🔍 Global Spirit &amp; Bottle Locator
            </h2>
            <span style={styles.globalSearchScopeBadge}>Venue-Wide &middot; Open &amp; Closed</span>
          </div>
          <p style={styles.globalSearchSub}>
            Find exact physical shelf locations for any spirit across the entire venue (Open Lounge Bar, Sealed Retail Shelves, or Back Storage).
          </p>
        </div>

        {/* Search Input Bar with Integrated Barcode Scan */}
        <div style={styles.globalSearchInputWrap}>
          <span style={styles.globalSearchIcon}>🔍</span>
          <input
            type="text"
            value={globalSearchQuery}
            onChange={(e) => setGlobalSearchQuery(e.target.value)}
            placeholder="Search any bottle by name, distillery, category, barcode UPC, SKU, or shelf slot (e.g. 'Buffalo Trace', 'Blanton', 'Weller', 'STOR-1A', '088004012478')…"
            style={styles.globalSearchInput}
          />
          <div style={styles.globalSearchInputActions}>
            <button
              type="button"
              onClick={() => {
                setTargetSlotForScan(null);
                setScannerOpen(true);
              }}
              style={styles.inlineScanIconBtn}
              title="Scan bottle barcode with camera to locate"
              aria-label="Scan barcode"
            >
              <BarcodeScanIcon size={20} />
            </button>
            {globalSearchQuery && (
              <button
                type="button"
                onClick={() => setGlobalSearchQuery("")}
                style={styles.globalSearchClearBtn}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Global Filter Chips */}
        <div style={styles.globalFilterRow}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setGlobalSearchTypeFilter("all")}
              style={{
                ...styles.globalFilterChip,
                ...(globalSearchTypeFilter === "all" ? styles.globalFilterChipActive : {}),
              }}
            >
              🌐 All Inventory ({globalCounts.total})
            </button>
            <button
              type="button"
              onClick={() => setGlobalSearchTypeFilter("open")}
              style={{
                ...styles.globalFilterChip,
                ...(globalSearchTypeFilter === "open" ? styles.globalFilterChipActiveOpen : {}),
              }}
            >
              🟢 Open / Pour ({globalCounts.openCount})
            </button>
            <button
              type="button"
              onClick={() => setGlobalSearchTypeFilter("retail")}
              style={{
                ...styles.globalFilterChip,
                ...(globalSearchTypeFilter === "retail" ? styles.globalFilterChipActiveRetail : {}),
              }}
            >
              🏷️ Closed Retail ({globalCounts.retailCount})
            </button>
            <button
              type="button"
              onClick={() => setGlobalSearchTypeFilter("storage")}
              style={{
                ...styles.globalFilterChip,
                ...(globalSearchTypeFilter === "storage" ? styles.globalFilterChipActiveStorage : {}),
              }}
            >
              📦 In Storage ({globalCounts.backStockCount})
            </button>
            <button
              type="button"
              onClick={() => setGlobalSearchTypeFilter("unplaced")}
              style={{
                ...styles.globalFilterChip,
                ...(globalSearchTypeFilter === "unplaced" ? styles.globalFilterChipActiveUnplaced : {}),
              }}
            >
              ⚪ Unplaced ({unplacedBottlesCount})
            </button>
            <button
              type="button"
              onClick={() => handleForceSquareSync()}
              disabled={syncingSquare}
              style={{
                ...styles.globalFilterChip,
                background: "rgba(249, 175, 54, 0.12)",
                borderColor: "var(--gold, #F9AF36)",
                color: "var(--gold, #F9AF36)",
                fontWeight: 700,
                cursor: syncingSquare ? "wait" : "pointer",
              }}
              title="Force an instant sync of all catalog items and variations from Square POS"
            >
              {syncingSquare ? "⏳ Syncing..." : "🔄 Sync from Square"}
            </button>
          </div>

          {globalSearchQuery.trim() && (
            <div style={styles.globalSearchCountText}>
              Found <strong>{globalSearchMatches.length}</strong> matching bottle{globalSearchMatches.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>

        {/* Instant Search Results Panel */}
        {globalSearchQuery.trim().length > 0 && (
          <div style={styles.globalResultsBox}>
            {globalSearchMatches.length === 0 ? (
              <div style={styles.globalEmptyState}>
                No bottles currently on shelf map matching &ldquo;{globalSearchQuery}&rdquo;.
                <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => handleOpenAddBottle("", "", globalSearchQuery)}
                    style={styles.addBtn}
                  >
                    + Add New Bottle to Shelf Map
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.globalResultsGrid}>
                {globalSearchMatches.map((b) => {
                  const isPour = Boolean(b.pour_price_cents && b.pour_price_cents > 0);
                  const isRetail = Boolean(b.retail_price_cents && b.retail_price_cents > 0);
                  const hasBackStock = (b.inventory_count && b.inventory_count > 1) || b.shelf_location.some((l) => l.startsWith("STOR-"));
                  const isPlaced = b.shelf_location.length > 0;

                  return (
                    <div
                      key={b.id}
                      onClick={() => {
                        setShelfPickerBottle(b);
                        if (isPlaced) {
                          const area = getAreaForSlot(b.shelf_location[0]);
                          setShelfPickerZoneId(area.zoneId);
                        }
                      }}
                      style={styles.globalBottleCard}
                    >
                      {/* Bottle Header & Metadata */}
                      <div style={styles.globalBottleHeader}>
                        <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                          <div style={styles.globalBottleName}>
                            {b.name.replace(/ - 1 oz$/i, "")}
                          </div>
                          <div style={styles.globalBottleMeta}>
                            <span style={{ fontWeight: 600, color: "var(--text)" }}>
                              {b.distillery || b.category || "Spirit"}
                            </span>
                            {b.category && b.distillery && <span>&middot; {b.category}</span>}
                          </div>
                          {(b.upc || b.sku) && (
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                              {b.upc && <span style={styles.miniUpcTag}>UPC: {b.upc}</span>}
                              {b.sku && <span style={styles.miniSkuTag}>SKU: {b.sku}</span>}
                            </div>
                          )}
                        </div>

                        {/* Price & State Badges */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end", flexShrink: 0 }}>
                          {isPour && (
                            <span style={styles.miniPourTag}>
                              🟢 ${(b.pour_price_cents! / 100).toFixed(2)} Pour
                            </span>
                          )}
                          {isRetail && (
                            <span style={styles.miniRetailTag}>
                              🏷️ ${(b.retail_price_cents! / 100).toFixed(2)} Bottle
                            </span>
                          )}
                          {hasBackStock && (
                            <span style={styles.miniBackStockTag} title="Back stock in Storage Room">
                              📦 {b.inventory_count ? `${b.inventory_count} in stock` : "Back Stock"}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Catalog Sizes & Variations */}
                      {b.variations && b.variations.length > 0 && (
                        <div style={{ marginTop: 8, padding: "8px 10px", background: "rgba(0,0,0,0.35)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)" }}>
                          <div style={{ fontSize: 10, fontWeight: 800, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
                            <span>🍾 Available Sizes &amp; Variations ({b.variations.length})</span>
                            <span style={{ fontSize: 9, opacity: 0.8, color: "var(--muted)" }}>Square Catalog</span>
                          </div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {b.variations.map((v) => (
                              <div
                                key={v.id}
                                style={{
                                  fontSize: 11,
                                  padding: "3px 8px",
                                  background: "rgba(255,255,255,0.04)",
                                  border: "1px solid rgba(255,255,255,0.09)",
                                  borderRadius: 6,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <span style={{ fontWeight: 800, color: "var(--text)" }}>{v.size_label || "Standard"}</span>
                                {v.bottle_price_cents ? (
                                  <span style={{ color: "#93c5fd", fontWeight: 700 }}>
                                    ${(v.bottle_price_cents / 100).toFixed(2)}
                                  </span>
                                ) : null}
                                {v.upc ? (
                                  <span style={{ fontSize: 9, opacity: 0.75, fontFamily: "monospace", color: "var(--gold)" }}>
                                    UPC: {v.upc}
                                  </span>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Locations Row */}
                      <div style={styles.globalLocationRow}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <div style={styles.globalLocationTitle}>
                            📍 Physical Locations ({b.shelf_location.length})
                          </div>
                          <span style={{ fontSize: 10, color: "var(--muted)" }}>Tap location to jump</span>
                        </div>

                        {!isPlaced ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={styles.unplacedBadge}>⚠️ Not Placed on Any Shelf</span>
                          </div>
                        ) : (
                          <div style={styles.locationPillsList}>
                            {b.shelf_location.map((loc) => {
                              const area = getAreaForSlot(loc);
                              const isCurrentZone = loc.startsWith(activeZoneObj.prefix);
                              return (
                                <button
                                  key={loc}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleJumpToSlot(loc);
                                  }}
                                  style={{
                                    ...styles.jumpLocationBtn,
                                    ...(area.isStorage
                                      ? styles.jumpLocationStorage
                                      : area.isOpen
                                      ? styles.jumpLocationOpen
                                      : styles.jumpLocationRetail),
                                    ...(isCurrentZone ? styles.jumpLocationActive : {}),
                                  }}
                                  title={`Click to jump directly to ${shelfCodeDisplay(loc)} on ${area.shelfLabel}`}
                                >
                                  <span>{area.isOpen ? "🟢" : area.isStorage ? "📦" : "🏷️"}</span>
                                  <span style={{ fontSize: 10, fontWeight: 800 }}>
                                    {area.isOpen ? "Open" : area.isStorage ? "Storage" : "Retail"}:
                                  </span>
                                  <strong>{area.shelfLabel}</strong>
                                  <span style={{ fontFamily: "monospace", letterSpacing: "0.03em" }}>{shelfCodeDisplay(loc)}</span>
                                  {isCurrentZone ? <span style={styles.viewingNowDot}>• on screen</span> : <span style={{ opacity: 0.7 }}>&rarr;</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Tap to Select / Manage Shelf Locations */}
                      <div style={styles.globalCardFooter}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShelfPickerBottle(b);
                            if (isPlaced) {
                              const area = getAreaForSlot(b.shelf_location[0]);
                              setShelfPickerZoneId(area.zoneId);
                            }
                          }}
                          style={styles.globalCardManageBtn}
                        >
                          📍 {isPlaced ? "Select / Change Shelf Locations" : "Place on Shelf"} &rarr;
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── VENUE ROOM TABS + SHELF ZONE SWITCHER RAIL ── */}
      <div style={{ marginBottom: 12 }}>
        {/* Room Filter Pills */}
        {/* 4 Venue Rooms: Equally spaced 4-column grid (Library, Parlor, Lounge, Storage) */}
        {/* Phones: 2×2 with wrapping labels — four nowrap "Library (3 Open · 2 Retail)" pills in
            one row ran past the screen edge and were clipped. */}
        <div style={{ display: "grid", gridTemplateColumns: isPhone ? "repeat(2, minmax(0, 1fr))" : "repeat(4, 1fr)", gap: 6, marginBottom: 8, alignItems: isPhone ? "stretch" : "center" }}>
          {MAIN_AREAS.map((area) => {
            const isAreaActive = area.zoneIds.includes(activeZone);
            const openCount = SHELF_ZONES.filter((z) => area.zoneIds.includes(z.id) && z.bottleType === "open").length;
            const retailCount = SHELF_ZONES.filter((z) => area.zoneIds.includes(z.id) && z.bottleType === "retail" && z.id !== "STOR").length;
            const storageCount = SHELF_ZONES.filter((z) => area.zoneIds.includes(z.id) && z.id === "STOR").length;

            let breakdown = `${area.zoneIds.length}`;
            if (openCount > 0 && retailCount > 0) {
              breakdown = `${openCount} Open · ${retailCount} Retail`;
            } else if (openCount > 0) {
              breakdown = `${openCount} Open`;
            } else if (storageCount > 0) {
              breakdown = `${storageCount} Storage`;
            }

            return (
              <button
                key={area.id}
                type="button"
                onClick={() => {
                  if (!area.zoneIds.includes(activeZone)) {
                    setActiveZone(area.defaultZoneId);
                    setSelectedSlotCode(null);
                  }
                }}
                style={{
                  ...styles.roomFilterPill,
                  ...(isAreaActive ? styles.roomFilterPillActive : {}),
                  ...(isPhone ? { whiteSpace: "normal", padding: "6px 4px" } : {}),
                }}
              >
                {area.icon} {area.label} ({breakdown})
              </button>
            );
          })}
        </div>

        {/* High-Density Shelf Rail (Filtered to Shelves in Selected Room) */}
        <div style={styles.compactZoneRailWrap}>
          <div style={styles.compactZoneRail}>
            {roomShelves.map((z) => {
              const isActive = z.id === activeZone;
              const isOpen = z.bottleType === "open";
              const isStorage = z.id === "STOR";
              const count = bottles.filter((b) => b.shelf_location.some((loc) => loc.startsWith(z.prefix))).length;
              const searchMatchesInZone = globalSearchMatches.filter((b) => b.shelf_location.some((loc) => loc.startsWith(z.prefix))).length;

              return (
                <button
                  key={z.id}
                  type="button"
                  onClick={() => {
                    setActiveZone(z.id);
                    setSelectedSlotCode(null);
                  }}
                  style={{
                    ...styles.compactZoneChip,
                    ...(isActive ? styles.compactZoneChipActive : {}),
                    ...(isOpen && !isActive
                      ? { borderLeft: "3px solid #22c55e" }
                      : isStorage && !isActive
                      ? { borderLeft: "3px solid #eab308" }
                      : !isActive
                      ? { borderLeft: "3px solid var(--gold, #F9AF36)" }
                      : {}),
                  }}
                >
                  <span style={{ fontSize: 13 }}>{z.icon}</span>
                  <span style={{ fontWeight: 700, fontSize: 12 }}>{z.label}</span>
                  <span
                    style={{
                      fontSize: 9,
                      padding: "1px 5px",
                      borderRadius: 3,
                      fontWeight: 800,
                      background: isOpen
                        ? "rgba(34,197,94,0.18)"
                        : isStorage
                        ? "rgba(234,179,8,0.18)"
                        : "rgba(249,175,54,0.18)",
                      color: isOpen ? "#4ade80" : isStorage ? "#fde047" : "var(--gold)",
                    }}
                  >
                    {isOpen ? "🟢 OPEN" : isStorage ? "📦 STORAGE" : "🏷️ RETAIL"}
                  </span>
                  <span style={{ fontSize: 10, opacity: 0.75, fontFamily: "monospace" }}>({z.prefix})</span>
                  {searchMatchesInZone > 0 && (
                    <span style={styles.zoneMatchDot}>✨{searchMatchesInZone}</span>
                  )}
                  <span style={isActive ? styles.compactZoneBadgeActive : styles.compactZoneBadge}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Studio Card */}
      <div style={styles.mainCard}>
        {/* ── COMPACT FLOOR STATUS & VIEW STRIP ── */}
        <div style={styles.floorStatusStrip}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: "var(--gold)" }}>
              {activeZoneObj.icon} {activeZoneObj.label}
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 800,
                padding: "2px 7px",
                borderRadius: 4,
                background:
                  activeZoneObj.bottleType === "open"
                    ? "rgba(34,197,94,0.18)"
                    : activeZoneObj.id === "STOR"
                    ? "rgba(234,179,8,0.18)"
                    : "rgba(249,175,54,0.18)",
                color:
                  activeZoneObj.bottleType === "open"
                    ? "#4ade80"
                    : activeZoneObj.id === "STOR"
                    ? "#fde047"
                    : "var(--gold)",
                border:
                  activeZoneObj.bottleType === "open"
                    ? "1px solid rgba(34,197,94,0.4)"
                    : activeZoneObj.id === "STOR"
                    ? "1px solid rgba(234,179,8,0.4)"
                    : "1px solid rgba(249,175,54,0.4)",
              }}
            >
              {activeZoneObj.id === "STOR"
                ? "📦 UNOPENED CASE STOCK"
                : activeZoneObj.bottleType === "open"
                ? "🟢 OPEN BOTTLE STATION (POURS)"
                : "🏷️ SEALED RETAIL DISPLAY"}
            </span>
            <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>
              {zoneStats.occupied}/{zoneStats.totalSlots} Slots ({zoneStats.totalPlacedBottles} bottles)
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              type="button"
              onClick={() => setCategoryFilter((prev) => (prev === "empty" ? "all" : "empty"))}
              style={{
                ...styles.miniFilterPill,
                ...(categoryFilter === "empty" ? styles.miniFilterPillActive : {}),
              }}
              title="Toggle empty slot filter"
            >
              ⚪ {categoryFilter === "empty" ? "All Slots" : "Empty Only"}
            </button>

            <div style={styles.viewToggleGroupMini}>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                style={{ ...styles.viewToggleBtnMini, ...(viewMode === "grid" ? styles.viewToggleActiveMini : {}) }}
                title="2D Shelf Grid View"
              >
                🗺️
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                style={{ ...styles.viewToggleBtnMini, ...(viewMode === "table" ? styles.viewToggleActiveMini : {}) }}
                title="List View"
              >
                📋
              </button>
            </div>

            <button
              type="button"
              onClick={() => handleOpenAddBottle()}
              style={styles.miniAddBtn}
              title="Add a new bottle to this zone"
            >
              + Add
            </button>
          </div>
        </div>

        {/* Main View Area */}
        {loading ? (
          <div style={styles.loader}>Loading physical shelf layout…</div>
        ) : viewMode === "grid" ? (
          renderPhysicalGrid()
        ) : (
          /* Table View */
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Shelf Code</th>
                  <th style={styles.th}>Bottle Name</th>
                  <th style={styles.th}>Type &amp; Pricing</th>
                  <th style={styles.th}>Square Count &amp; Back Stock</th>
                  <th style={styles.th}>Distillery / Category</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredBottles.map((b) => {
                  const hasBackStock = (b.inventory_count && b.inventory_count > 1) || b.shelf_location.some((loc) => loc.startsWith("STOR-"));
                  return (
                    <tr key={b.id} style={styles.tr}>
                      <td style={styles.td}>
                        {b.shelf_location.length > 0 ? (
                          b.shelf_location.map((loc) => (
                            <span key={loc} style={styles.codeBadge}>
                              {loc}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: "var(--muted)", fontStyle: "italic", fontSize: 11 }}>
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <strong style={{ color: "var(--text)" }}>{b.name}</strong>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {b.pour_price_cents ? (
                            <span style={styles.typeBadgeOpen}>
                              🟢 ${(b.pour_price_cents / 100).toFixed(2)} pour
                            </span>
                          ) : null}
                          {b.retail_price_cents ? (
                            <span style={styles.typeBadgeRetail}>
                              🏷️ ${(b.retail_price_cents / 100).toFixed(2)} btl
                            </span>
                          ) : null}
                          {!b.pour_price_cents && !b.retail_price_cents && (
                            <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: b.inventory_count && b.inventory_count > 0 ? "#86efac" : "var(--muted)" }}>
                            {b.inventory_count != null ? `${b.inventory_count} on-hand` : "—"}
                          </span>
                          {hasBackStock && (
                            <span style={styles.backStockTagBadge}>
                              📦 Back Stock
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>{b.distillery || b.category || "—"}</td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedLocatorBottle(b);
                              setLocatorSearchQuery(b.name);
                              setLocatorOpen(true);
                            }}
                            style={styles.inspectBtn}
                            title="View all venue locations and related bottles for this spirit"
                          >
                            📍 Find All
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedSlotCode(b.shelf_location[0] || `${activeZoneObj.prefix}-1A`);
                            }}
                            style={styles.reassignBtn}
                          >
                            Manage Slot
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredBottles.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...styles.td, textAlign: "center", color: "var(--muted)", padding: 24 }}>
                      No bottles match query.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══ VENUE BOTTLE LOCATOR MODAL (Multi-Location & Back Stock Finder) ═══ */}
      {locatorOpen && (
        <div className="mobile-sheet-backdrop" style={styles.modalBackdrop} onClick={() => setLocatorOpen(false)}>
          <div className="mobile-sheet-content" style={{ ...styles.modalCard, maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />

            <div style={{ ...styles.slotModalHeader, position: "sticky", top: 0, zIndex: 30, background: "var(--surface, #18181b)", paddingBottom: 10, marginBottom: 12, borderBottom: "1px solid var(--border, #27272a)" }}>
              <div>
                <h3 style={styles.modalTitle}>📍 Venue Bottle Locator</h3>
                <p style={styles.modalSub}>
                  Find physical placements across Library, Parlor, Lounge, and Storage
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLocatorOpen(false)}
                style={styles.closeBtn}
                title="Close locator"
              >
                ✕
              </button>
            </div>

            {/* Quick Search & Barcode Scan Bar */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="text"
                  value={locatorSearchQuery}
                  onChange={(e) => {
                    setLocatorSearchQuery(e.target.value);
                  }}
                  placeholder="Type 2+ letters to search bottle catalog / scan barcode…"
                  style={{ ...styles.modalInput, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setTargetSlotForScan(null);
                    setScannerOpen(true);
                  }}
                  style={styles.slotScanInputBtn}
                  title="Scan bottle barcode to inspect venue locations"
                >
                  📷 Scan
                </button>
              </div>

              {/* Autocomplete dropdown if typing */}
              {locatorSearchQuery.trim().length >= 2 && (!selectedLocatorBottle || locatorSearchQuery !== selectedLocatorBottle.name) && (
                <div style={styles.searchResultsBox}>
                  {locatorMatches.length === 0 ? (
                    <div style={{ padding: 12, fontSize: 12, color: "var(--muted)", textAlign: "center" }}>
                      No bottles match &ldquo;{locatorSearchQuery}&rdquo;.
                    </div>
                  ) : (
                    locatorMatches.map((b) => {
                      const isPour = Boolean(b.pour_price_cents && b.pour_price_cents > 0) || b.name.toLowerCase().includes("1 oz");
                      const isRetail = Boolean(b.retail_price_cents && b.retail_price_cents > 0) || Boolean(b.upc);
                      return (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => {
                            setSelectedLocatorBottle(b);
                            setLocatorSearchQuery(b.name);
                          }}
                          style={styles.searchResultItem}
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0, paddingRight: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>
                                {b.name}
                              </span>
                              {isRetail && b.retail_price_cents ? (
                                <span style={styles.miniRetailTag}>
                                  🍾 ${(b.retail_price_cents / 100).toFixed(2)} Bottle
                                </span>
                              ) : null}
                              {isPour && b.pour_price_cents ? (
                                <span style={styles.miniPourTag}>
                                  🥃 ${(b.pour_price_cents / 100).toFixed(2)} Pour
                                </span>
                              ) : null}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 11, color: "var(--muted)" }}>
                              <span>{b.distillery || b.category}</span>
                              {b.upc ? <span style={styles.miniUpcTag}>UPC: {b.upc}</span> : <span style={{ color: "#666", fontSize: 10 }}>No Barcode</span>}
                              {b.sku ? <span style={styles.miniSkuTag}>SKU: {b.sku}</span> : null}
                              {b.shelf_location.length > 0 ? (
                                <span style={styles.miniLocTag}>📍 {b.shelf_location.join(", ")}</span>
                              ) : (
                                <span style={{ color: "#777", fontSize: 10 }}>⚪ 0 Placed</span>
                              )}
                            </div>
                          </div>
                          <span style={styles.badgeAssign}>Inspect &rarr;</span>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Selected Bottle Multi-Location Inspector */}
            {selectedLocatorBottle ? (
              <div>
                {/* Bottle Header Card */}
                <div style={styles.locatorBottleBanner}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
                      {selectedLocatorBottle.name.replace(/ - 1 oz$/i, "")}
                    </h4>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                      {selectedLocatorBottle.distillery || selectedLocatorBottle.category}
                      {selectedLocatorBottle.upc && <span> &middot; UPC: <code style={{ color: "var(--gold)" }}>{selectedLocatorBottle.upc}</code></span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    {selectedLocatorBottle.pour_price_cents ? (
                      <span style={styles.typeBadgeOpen}>
                        🟢 ${(selectedLocatorBottle.pour_price_cents / 100).toFixed(2)} Pour
                      </span>
                    ) : null}
                    {selectedLocatorBottle.retail_price_cents ? (
                      <span style={styles.typeBadgeRetail}>
                        🏷️ ${(selectedLocatorBottle.retail_price_cents / 100).toFixed(2)} Retail
                      </span>
                    ) : null}
                  </div>
                </div>

                {/* Back Storage & Square Inventory Summary Strip */}
                <div style={styles.backStorageStockBanner}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18 }}>📦</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text)" }}>
                        Square Inventory: {selectedLocatorBottle.inventory_count != null ? `${selectedLocatorBottle.inventory_count} Total Units` : "Count Not Set"}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>
                        {selectedLocatorBottle.inventory_count && selectedLocatorBottle.inventory_count > selectedLocatorBottle.shelf_location.length ? (
                          <span style={{ color: "#86efac", fontWeight: 700 }}>
                            ✓ Back Stock Available in Storage Room (~{selectedLocatorBottle.inventory_count - selectedLocatorBottle.shelf_location.length} extra bottles)
                          </span>
                        ) : selectedLocatorBottle.shelf_location.some((loc) => loc.startsWith("STOR-")) ? (
                          <span style={{ color: "#86efac", fontWeight: 700 }}>✓ Placed in Back Storage Room Racks</span>
                        ) : selectedLocatorBottle.inventory_count && selectedLocatorBottle.inventory_count <= 1 ? (
                          <span style={{ color: "#facc15" }}>⚠️ Low Stock — Only floor placements active</span>
                        ) : (
                          <span>Physical shelf placements shown below</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pour count actions (Phase 3c). Self-contained: it fetches its own pour state
                    because the browser role cannot read pour_count_version, and renders nothing
                    at all for a bottle that is not poured by the ounce. */}
                <BottlePourCount
                  bottleItemId={selectedLocatorBottle.id}
                  bottleName={selectedLocatorBottle.name.replace(/ - \d+(\.\d+)? oz$/i, "")}
                />

                {/* Primary Physical Locations List */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <label style={{ ...styles.label, marginBottom: 0 }}>
                      Physical Placements in Venue ({selectedLocatorBottle.shelf_location.length})
                    </label>
                    <span style={{ fontSize: 11, color: "var(--muted)" }}>
                      {selectedLocatorBottle.shelf_location.length === 0 ? "⚠️ Not placed on any shelf" : "Active locations"}
                    </span>
                  </div>

                  {selectedLocatorBottle.shelf_location.length === 0 ? (
                    <div style={styles.emptySlotBox}>
                      This bottle is currently unassigned to any shelf slot in the venue.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {selectedLocatorBottle.shelf_location.map((loc) => {
                        const zone = [...SHELF_ZONES]
                          .sort((a, b) => b.prefix.length - a.prefix.length)
                          .find((z) => loc.startsWith(z.prefix)) || SHELF_ZONES[0];
                        const area = MAIN_AREAS.find((a) => a.zoneIds.includes(zone.id)) || MAIN_AREAS[0];

                        return (
                          <div key={loc} style={styles.locationPlacementCard}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontSize: 20 }}>{zone.icon}</span>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
                                  {area.label}: {zone.label} ({loc})
                                </div>
                                <div style={{ fontSize: 11, color: "var(--muted)", display: "flex", gap: 6, alignItems: "center", marginTop: 2 }}>
                                  <span>Slot {shelfCodeDisplay(loc)}</span>
                                  <span>&middot;</span>
                                  <span style={{ color: zone.bottleType === "open" ? "#86efac" : "#93c5fd" }}>
                                    {zone.bottleType === "open" ? "🟢 Open Bottle" : "🏷️ Sealed Retail"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <button
                                type="button"
                                onClick={() => handleJumpToSlot(loc)}
                                style={styles.jumpSlotBtn}
                                title="Open this shelf and highlight slot in 2D map"
                              >
                                🗺️ View on Map
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveFromSlot(selectedLocatorBottle.id, loc)}
                                disabled={saving}
                                style={styles.removeSlotBtn}
                                title="Remove bottle from this shelf"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Quick Add To Another Shelf Slot */}
                <div style={styles.addAnotherSlotBox}>
                  <label style={{ ...styles.label, marginBottom: 6 }}>+ Place Spirit in Another Shelf Slot or Back Storage</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <input
                      type="text"
                      placeholder="e.g. BAR-1A, LIB-3C, RES-1B, LIB-A-1A, STOR-1A"
                      value={newLocationSlotInput}
                      onChange={(e) => setNewLocationSlotInput(e.target.value.toUpperCase())}
                      style={{ ...styles.modalInput, flex: 1, minWidth: 160 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!newLocationSlotInput.trim()) {
                          showToast("Please enter a slot code", "err");
                          return;
                        }
                        handleAssignToSlot(selectedLocatorBottle.id, newLocationSlotInput.trim());
                        setNewLocationSlotInput("");
                      }}
                      disabled={saving || !newLocationSlotInput.trim()}
                      style={styles.saveBtn}
                    >
                      + Assign Slot
                    </button>
                  </div>
                </div>

                {/* Related Spirits / Bottles in Venue */}
                {relatedLocatorBottles.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <label style={styles.label}>
                      Related Bottles &amp; Stock in Venue ({relatedLocatorBottles.length})
                    </label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 150, overflowY: "auto" }}>
                      {relatedLocatorBottles.map((rel) => (
                        <div key={rel.id} style={styles.relatedBottleRow}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>
                              {rel.name}
                            </div>
                            <div style={{ fontSize: 11, color: "var(--muted)" }}>
                              {rel.shelf_location.length > 0 ? (
                                <span>Placed at: {rel.shelf_location.map((l) => shelfCodeDisplay(l)).join(", ")}</span>
                              ) : (
                                <span style={{ fontStyle: "italic" }}>No shelf assigned</span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedLocatorBottle(rel);
                              setLocatorSearchQuery(rel.name);
                            }}
                            style={styles.inspectBtn}
                          >
                            Inspect &rarr;
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: 24, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
                🔍 Type a bottle name or click &ldquo;📷 Scan&rdquo; above to locate all open and closed bottles in the venue.
              </div>
            )}

            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setLocatorOpen(false)}
                style={styles.saveBtn}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ INTERACTIVE BOTTLE SHELF LOCATION SELECTOR SHEET ═══ */}
      {shelfPickerBottle && (
        <div className="mobile-sheet-backdrop" style={styles.modalBackdrop} onClick={() => setShelfPickerBottle(null)}>
          <div className="mobile-sheet-content" style={{ ...styles.modalCard, maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />

            {/* Modal Header */}
            <div style={{ ...styles.slotModalHeader, position: "sticky", top: 0, zIndex: 30, background: "var(--surface, #18181b)", paddingBottom: 10, marginBottom: 12, borderBottom: "1px solid var(--border, #27272a)" }}>
              <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  📍 Shelf Location Selector
                </div>
                <h3 style={{ ...styles.modalTitle, fontSize: 18, marginTop: 2 }}>
                  {shelfPickerBottle.name.replace(/ - 1 oz$/i, "")}
                </h3>
                <div style={{ fontSize: 12, color: "var(--muted)", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 2 }}>
                  <span style={{ fontWeight: 600, color: "var(--text)" }}>{shelfPickerBottle.distillery || shelfPickerBottle.category || "Spirit"}</span>
                  {shelfPickerBottle.category && shelfPickerBottle.distillery && <span>&middot; {shelfPickerBottle.category}</span>}
                  {shelfPickerBottle.pour_price_cents && (
                    <span style={{ color: "#86efac" }}>🟢 ${(shelfPickerBottle.pour_price_cents / 100).toFixed(2)} pour</span>
                  )}
                  {shelfPickerBottle.retail_price_cents && (
                    <span style={{ color: "#93c5fd" }}>🏷️ ${(shelfPickerBottle.retail_price_cents / 100).toFixed(2)} btl</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShelfPickerBottle(null)}
                style={styles.closeBtn}
                title="Close sheet"
              >
                ✕
              </button>
            </div>

            {/* Sizes & Variations in Catalog */}
            {shelfPickerBottle.variations && shelfPickerBottle.variations.length > 0 && (
              <div style={{ padding: "10px 14px", background: "rgba(0,0,0,0.35)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                  🍾 Available Sizes &amp; Variations ({shelfPickerBottle.variations.length})
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {shelfPickerBottle.variations.map((v) => (
                    <div
                      key={v.id}
                      style={{
                        fontSize: 11,
                        padding: "4px 9px",
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: 6,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span style={{ fontWeight: 800, color: "var(--text)" }}>{v.size_label || "Standard"}</span>
                      {v.bottle_price_cents ? (
                        <span style={{ color: "#93c5fd", fontWeight: 700 }}>
                          ${(v.bottle_price_cents / 100).toFixed(2)}
                        </span>
                      ) : null}
                      {v.upc ? (
                        <span style={{ fontSize: 9, opacity: 0.75, fontFamily: "monospace", color: "var(--gold)" }}>
                          UPC: {v.upc}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Current Physical Placements Section */}
            <div style={{ padding: "12px 14px", background: "rgba(0,0,0,0.35)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)", marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "var(--text)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Current Placements ({shelfPickerBottle.shelf_location.length})
                </span>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>
                  {shelfPickerBottle.shelf_location.length === 0 ? "Not placed yet" : "Tap slot to jump or ✕ to remove"}
                </span>
              </div>

              {shelfPickerBottle.shelf_location.length === 0 ? (
                <div style={{ padding: "10px 12px", background: "rgba(239,68,68,0.1)", border: "1px dashed rgba(239,68,68,0.3)", borderRadius: 8, color: "#fca5a5", fontSize: 12 }}>
                  ⚠️ This bottle is not currently placed on any shelf. Select a shelf zone and slot below to assign it.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {shelfPickerBottle.shelf_location.map((loc) => {
                    const area = getAreaForSlot(loc);
                    return (
                      <div key={loc} style={styles.locationPlacementCard}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 18 }}>{area.icon}</span>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
                              {area.areaLabel}: {area.shelfLabel}
                            </div>
                            <div style={{ fontSize: 11, color: "var(--gold)", fontFamily: "monospace", marginTop: 2 }}>
                              Slot: {shelfCodeDisplay(loc)} ({loc})
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <button
                            type="button"
                            onClick={() => {
                              handleJumpToSlot(loc);
                              setShelfPickerBottle(null);
                            }}
                            style={styles.jumpSlotBtn}
                            title="View on 2D map"
                          >
                            🗺️ Jump to Slot
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveFromSlot(shelfPickerBottle.id, loc)}
                            disabled={saving}
                            style={styles.removeSlotBtn}
                            title="Remove from this shelf"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Step 1: Select Venue Shelf Zone */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ ...styles.label, marginBottom: 8 }}>
                Step 1: Choose Room &amp; Shelf Display
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 8 }}>
                {SHELF_ZONES.map((zone) => {
                  const isZoneSelected = zone.id === shelfPickerZoneId;
                  const zoneArea = MAIN_AREAS.find((a) => a.zoneIds.includes(zone.id));
                  const isPlacedInZone = shelfPickerBottle.shelf_location.some((l) => l.startsWith(zone.prefix));

                  return (
                    <button
                      key={zone.id}
                      type="button"
                      onClick={() => setShelfPickerZoneId(zone.id)}
                      style={{
                        padding: "10px 8px",
                        background: isZoneSelected ? "rgba(249, 175, 54, 0.18)" : "rgba(255,255,255,0.03)",
                        border: isZoneSelected ? "1.5px solid var(--gold)" : "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 8,
                        color: isZoneSelected ? "var(--gold)" : "var(--text)",
                        textAlign: "left",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        transition: "all 0.15s ease",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 16 }}>{zone.icon}</span>
                        {isPlacedInZone && (
                          <span style={{ fontSize: 9, fontWeight: 800, background: "#27ae60", color: "#fff", padding: "1px 4px", borderRadius: 4 }}>
                            PLACED
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 800, marginTop: 4 }}>{zone.label}</div>
                      <div style={{ fontSize: 10, color: "var(--muted)" }}>{zoneArea?.label || ""} ({zone.prefix})</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Select Slot Grid in Selected Zone */}
            {(() => {
              const currentZoneObj = SHELF_ZONES.find((z) => z.id === shelfPickerZoneId) || SHELF_ZONES[0];
              const reversedRows = currentZoneObj.rows.split("").reverse();

              return (
                <div style={{ padding: "12px 14px", background: "rgba(0,0,0,0.4)", borderRadius: 10, border: "1px solid rgba(249,175,54,0.2)", marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <label style={{ ...styles.label, marginBottom: 0 }}>
                      Step 2: Tap a Slot on {currentZoneObj.label} to Assign
                    </label>
                    <span style={{ fontSize: 11, color: "var(--gold)" }}>
                      {currentZoneObj.cols} Col × {currentZoneObj.rows.length} Tiers
                    </span>
                  </div>

                  {/* Visual Slot Selector Matrix */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto", paddingRight: 4 }}>
                    {reversedRows.map((rowLetter) => (
                      <div key={rowLetter} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ width: 22, fontSize: 11, fontWeight: 800, color: "var(--gold)", textAlign: "center" }}>
                          {rowLetter}
                        </span>
                        <div style={{ display: "grid", gridTemplateColumns: `repeat(${currentZoneObj.cols}, 1fr)`, gap: 6, flex: 1 }}>
                          {Array.from({ length: currentZoneObj.cols }, (_, i) => i + 1).map((colNum) => {
                            const slotCode = `${currentZoneObj.prefix}-${colNum}${rowLetter}`;
                            const isAlreadyPlacedHere = shelfPickerBottle.shelf_location.includes(slotCode);
                            const itemsInSlot = locationMap[slotCode] || [];

                            return (
                              <button
                                key={slotCode}
                                type="button"
                                onClick={() => {
                                  if (isAlreadyPlacedHere) {
                                    handleRemoveFromSlot(shelfPickerBottle.id, slotCode);
                                  } else {
                                    handleAssignToSlot(shelfPickerBottle.id, slotCode);
                                  }
                                }}
                                disabled={saving}
                                style={{
                                  padding: "8px 4px",
                                  background: isAlreadyPlacedHere
                                    ? "rgba(39, 174, 96, 0.25)"
                                    : itemsInSlot.length > 0
                                    ? "rgba(249, 175, 54, 0.08)"
                                    : "rgba(255,255,255,0.03)",
                                  border: isAlreadyPlacedHere
                                    ? "1.5px solid #27ae60"
                                    : itemsInSlot.length > 0
                                    ? "1px solid rgba(249, 175, 54, 0.3)"
                                    : "1px dashed rgba(255,255,255,0.1)",
                                  borderRadius: 6,
                                  color: isAlreadyPlacedHere ? "#86efac" : "var(--text)",
                                  cursor: "pointer",
                                  textAlign: "center",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 2,
                                  transition: "all 0.15s ease",
                                }}
                                title={
                                  isAlreadyPlacedHere
                                    ? "Click to remove bottle from this slot"
                                    : `Click to assign bottle to ${slotCode} (${itemsInSlot.length} spirits already here)`
                                }
                              >
                                <span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 800 }}>
                                  {shelfCodeDisplay(slotCode)}
                                </span>
                                <span style={{ fontSize: 9, opacity: 0.8 }}>
                                  {isAlreadyPlacedHere ? "✓ Placed" : itemsInSlot.length > 0 ? `${itemsInSlot.length} btls` : "Empty"}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Manual Slot Code Entry */}
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="text"
                      placeholder={`Or type exact slot code (e.g. ${currentZoneObj.prefix}-1A)`}
                      value={shelfPickerCustomInput}
                      onChange={(e) => setShelfPickerCustomInput(e.target.value.toUpperCase())}
                      style={{ ...styles.modalInput, flex: 1, minWidth: 160, padding: "8px 12px", fontSize: 12 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!shelfPickerCustomInput.trim()) return;
                        handleAssignToSlot(shelfPickerBottle.id, shelfPickerCustomInput.trim());
                        setShelfPickerCustomInput("");
                      }}
                      disabled={saving || !shelfPickerCustomInput.trim()}
                      style={{ ...styles.saveBtn, padding: "8px 14px", fontSize: 12 }}
                    >
                      + Assign
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Modal Actions */}
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setShelfPickerBottle(null)}
                style={styles.saveBtn}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SLOT DETAIL & ASSIGNMENT SHEET ═══ */}
      {selectedSlotCode && (
        <div className="mobile-sheet-backdrop" style={styles.modalBackdrop} onClick={() => setSelectedSlotCode(null)}>
          <div className="mobile-sheet-content" style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />

            <div style={{ ...styles.slotModalHeader, position: "sticky", top: 0, zIndex: 30, background: "var(--surface, #18181b)", paddingBottom: 10, marginBottom: 12, borderBottom: "1px solid var(--border, #27272a)" }}>
              <div>
                <h3 style={styles.modalTitle}>
                  Slot {selectedSlotCode} ({shelfCodeDisplay(selectedSlotCode)})
                </h3>
                <p style={styles.modalSub}>
                  {activeZoneObj.label} &middot; {activeAreaObj.label} ({activeAreaObj.bottleType === "open" ? "Open Bottles" : "Retail Closed"})
                </p>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => handleOpenAuditHistory(selectedSlotCode)}
                  style={styles.historySlotBtn}
                  title="View previous audit history and unreplaced bottles for this slot"
                >
                  📜 History
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleOpenAudit(selectedSlotCode);
                  }}
                  style={styles.auditSlotBtn}
                  title="Rescan and audit the exact spirit lineup for this shelf"
                >
                  🔍 Audit Lineup
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedSlotCode(null)}
                  style={styles.closeBtn}
                  title="Close sheet"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* ── 1. TOP QUICK ACTION: Barcode Scanner & Search / Place Spirit ── */}
            <div style={{
              background: "rgba(249, 175, 54, 0.08)",
              border: "1px solid rgba(249, 175, 54, 0.3)",
              borderRadius: 12,
              padding: "12px 14px",
              marginBottom: 16,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ ...styles.label, marginBottom: 0, color: "var(--gold, #F9AF36)", fontSize: 13, fontWeight: 800 }}>
                  ⚡ Quick Place to {selectedSlotCode} ({shelfCodeDisplay(selectedSlotCode)})
                </label>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>Scan barcode or search</span>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="text"
                  value={slotSearchQuery}
                  onChange={(e) => setSlotSearchQuery(e.target.value)}
                  placeholder="Type bottle name or search catalog…"
                  style={{ ...styles.modalInput, flex: 1, background: "var(--surface, #18181b)", borderColor: "rgba(249, 175, 54, 0.35)" }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setTargetSlotForScan(selectedSlotCode);
                    setScannerOpen(true);
                  }}
                  style={{
                    ...styles.slotScanInputBtn,
                    padding: "10px 16px",
                    boxShadow: "0 2px 10px rgba(249, 175, 54, 0.25)",
                  }}
                  title="Scan bottle barcode directly into this shelf slot"
                >
                  📷 Scan Barcode
                </button>
              </div>

              {slotSearchQuery.trim().length >= 2 && (
                <div style={styles.searchResultsBox}>
                  {slotCatalogMatches.length === 0 ? (
                    <div style={{ padding: 12, fontSize: 12, color: "var(--muted)", textAlign: "center" }}>
                      No matches found in catalog.
                    </div>
                  ) : (
                    slotCatalogMatches.map((b) => {
                      const alreadyInSlot = b.shelf_location.includes(selectedSlotCode);
                      const isPour = Boolean(b.pour_price_cents && b.pour_price_cents > 0) || b.name.toLowerCase().includes("1 oz");
                      const isRetail = Boolean(b.retail_price_cents && b.retail_price_cents > 0) || Boolean(b.upc);
                      return (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => handleAssignToSlot(b.id, selectedSlotCode)}
                          disabled={saving || alreadyInSlot}
                          style={{
                            ...styles.searchResultItem,
                            ...(alreadyInSlot ? { opacity: 0.5, cursor: "default" } : {}),
                          }}
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0, paddingRight: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>
                                {b.name}
                              </span>
                              {isRetail && b.retail_price_cents ? (
                                <span style={styles.miniRetailTag}>
                                  🍾 ${(b.retail_price_cents / 100).toFixed(2)} Bottle
                                </span>
                              ) : null}
                              {isPour && b.pour_price_cents ? (
                                <span style={styles.miniPourTag}>
                                  🥃 ${(b.pour_price_cents / 100).toFixed(2)} Pour
                                </span>
                              ) : null}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 11, color: "var(--muted)" }}>
                              <span>{b.distillery || b.category}</span>
                              {b.upc ? <span style={styles.miniUpcTag}>UPC: {b.upc}</span> : <span style={{ color: "#666", fontSize: 10 }}>No Barcode</span>}
                              {b.sku ? <span style={styles.miniSkuTag}>SKU: {b.sku}</span> : null}
                              {b.shelf_location.length > 0 ? (
                                <span style={styles.miniLocTag}>📍 Placed on {b.shelf_location.join(", ")}</span>
                              ) : (
                                <span style={{ color: "#777", fontSize: 10 }}>⚪ Unplaced</span>
                              )}
                            </div>
                          </div>
                          <span style={alreadyInSlot ? styles.badgeAlready : styles.badgeAssign}>
                            {alreadyInSlot ? "Placed Here" : "+ Place Here"}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Unreplaced Empty Bottles Alert from Previous Audits */}
            {(() => {
              const recentAudit = auditHistoryRecords.find(
                (h) => (h.slot_code === selectedSlotCode || h.from_slot === selectedSlotCode) && h.action === "shelf_audit"
              );
              const unreplaced = (recentAudit?.missing_bottles || []).filter(
                (m) => !(locationMap[selectedSlotCode] || []).some((b) => b.id === m.id)
              );
              if (!unreplaced.length) return null;
              return (
                <div style={styles.unreplacedWarningCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#fca5a5" }}>
                      ⚠️ Unreplaced / Empty from Last Audit ({unreplaced.length})
                    </span>
                    <span style={{ fontSize: 10, color: "var(--muted)" }}>
                      {recentAudit?.auditor_name ? `by ${recentAudit.auditor_name}` : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {unreplaced.map((m) => (
                      <div key={m.id} style={styles.unreplacedItemRow}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#fca5a5" }}>
                          ⭕ {m.name.replace(/ - 1 oz$/i, "")}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleAssignToSlot(m.id, selectedSlotCode)}
                          style={styles.restockQuickBtn}
                          title="Bottle was restocked on this shelf"
                        >
                          + Mark Restocked
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Currently Placed Bottles */}
            <div style={styles.slotPlacedSection}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <label style={{ ...styles.label, marginBottom: 0 }}>
                  Assigned Spirits ({ (locationMap[selectedSlotCode] || []).length })
                </label>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>Location mapping for staff</span>
              </div>
              {(locationMap[selectedSlotCode] || []).length === 0 ? (
                <div style={styles.emptySlotBox}>No spirits currently assigned to this shelf slot.</div>
              ) : (
                <div style={styles.placedList}>
                  {(locationMap[selectedSlotCode] || []).map((b) => {
                    const hasBackStock = (b.inventory_count && b.inventory_count > 1) || b.shelf_location.some((l) => l.startsWith("STOR-"));
                    return (
                      <div key={b.id} style={styles.placedItemRow}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.placedName}>{b.name.replace(/ - 1 oz$/i, "")}</div>
                          <div style={styles.placedMeta}>
                            {b.distillery || b.category} &middot;{" "}
                            {b.pour_price_cents ? (
                              <span style={{ color: "#86efac" }}>🟢 ${(b.pour_price_cents / 100).toFixed(2)} pour</span>
                            ) : b.retail_price_cents ? (
                              <span style={{ color: "#93c5fd" }}>🏷️ ${(b.retail_price_cents / 100).toFixed(2)} btl</span>
                            ) : (
                              "Spirit"
                            )}
                            {hasBackStock && (
                              <span style={{ color: "#facc15", marginLeft: 6 }}>
                                &middot; 📦 Back Stock in Storage
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          {isOwner && (
                            <button
                              type="button"
                              onClick={() => {
                                setSmartMoveBottle(b);
                                setSmartMoveFromSlot(selectedSlotCode);
                                setSmartMoveToSlot("");
                                setSmartMoveNotes("");
                              }}
                              style={styles.smartMoveBtn}
                              title="Owner: Acknowledge and move bottle to another shelf slot"
                            >
                              ⇄ Move
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedLocatorBottle(b);
                              setLocatorSearchQuery(b.name);
                              setSelectedSlotCode(null);
                              setLocatorOpen(true);
                            }}
                            style={styles.inspectBtn}
                            title="Find all other locations where this bottle is placed in venue"
                          >
                            📍 Find All
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveFromSlot(b.id, selectedSlotCode)}
                            disabled={saving}
                            style={styles.removeSlotBtn}
                            title="Remove from this shelf"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setSelectedSlotCode(null)}
                style={styles.saveBtn}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MULTIPLE VARIATIONS PICKER MODAL ═══ */}
      {variationPickerOpen && (
        <div className="mobile-sheet-backdrop" style={styles.modalBackdrop} onClick={() => setVariationPickerOpen(false)}>
          <div className="mobile-sheet-content" style={{ ...styles.modalCard, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />

            <div style={{ ...styles.slotModalHeader, position: "sticky", top: 0, zIndex: 30, background: "var(--surface, #18181b)", paddingBottom: 10, marginBottom: 12, borderBottom: "1px solid var(--border, #27272a)" }}>
              <div>
                <h3 style={styles.modalTitle}>
                  🍾 Select Bottle Variation ({variationCandidates.length} available)
                </h3>
                <p style={styles.modalSub}>
                  {variationTargetSlot
                    ? `Assigning to slot ${variationTargetSlot} (${shelfCodeDisplay(variationTargetSlot)})`
                    : `Barcode / Spirit: ${scannedBarcodeForPicker}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setVariationPickerOpen(false)}
                style={styles.closeBtn}
                title="Close picker"
              >
                ✕
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "65vh", overflowY: "auto", paddingRight: 4 }}>
              {variationCandidates.map((b) => {
                const alreadyInSlot = variationTargetSlot ? b.shelf_location.includes(variationTargetSlot) : false;
                const isPour = Boolean(b.pour_price_cents && b.pour_price_cents > 0) || b.name.toLowerCase().includes("1 oz");
                const isRetail = Boolean(b.retail_price_cents && b.retail_price_cents > 0) || Boolean(b.upc);
                const hasBackStock = (b.inventory_count && b.inventory_count > 1) || b.shelf_location.some((l) => l.startsWith("STOR-"));

                return (
                  <div
                    key={b.id}
                    style={{
                      background: "var(--surface, #18181b)",
                      border: "1px solid rgba(249, 175, 54, 0.3)",
                      borderRadius: 12,
                      padding: 14,
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text, #fff)", lineHeight: 1.3 }}>
                          {b.name}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--muted, #a1a1aa)", marginTop: 2 }}>
                          {b.distillery || b.category}
                        </div>
                        {(b.upc || b.sku) && (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                            {b.upc && <span style={styles.miniUpcTag}>UPC: {b.upc}</span>}
                            {b.sku && <span style={styles.miniSkuTag}>SKU: {b.sku}</span>}
                          </div>
                        )}
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end", flexShrink: 0 }}>
                        {isRetail && b.retail_price_cents && (
                          <span style={styles.miniRetailTag}>
                            🍾 ${(b.retail_price_cents / 100).toFixed(2)} Bottle
                          </span>
                        )}
                        {isPour && b.pour_price_cents && (
                          <span style={styles.miniPourTag}>
                            🥃 ${(b.pour_price_cents / 100).toFixed(2)} Pour
                          </span>
                        )}
                        {hasBackStock && (
                          <span style={styles.miniBackStockTag}>
                            📦 {b.inventory_count ? `${b.inventory_count} on-hand` : "In Storage"}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 6, borderTop: "1px solid var(--border, #27272a)", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ fontSize: 12, color: "var(--muted)" }}>
                        {b.shelf_location.length > 0 ? (
                          <span style={{ color: "#93c5fd" }}>📍 Placed on {b.shelf_location.map(shelfCodeDisplay).join(", ")}</span>
                        ) : (
                          <span style={{ color: "#777" }}>⚪ Unplaced</span>
                        )}
                      </div>

                      {variationTargetSlot ? (
                        <button
                          type="button"
                          onClick={async () => {
                            setVariationPickerOpen(false);
                            await handleAssignToSlot(b.id, variationTargetSlot);
                          }}
                          disabled={saving || alreadyInSlot}
                          style={{
                            ...styles.saveBtn,
                            padding: "8px 16px",
                            fontSize: 13,
                            ...(alreadyInSlot ? { opacity: 0.5, cursor: "default" } : {}),
                          }}
                        >
                          {alreadyInSlot ? "✓ Already Placed Here" : `+ Place in ${variationTargetSlot}`}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setVariationPickerOpen(false);
                            const cleanName = b.name.replace(/\s*-\s*\d+(\.\d+)?\s*oz$/i, "").trim();
                            setGlobalSearchQuery(cleanName);
                            if (b.shelf_location.length > 0) {
                              const firstSlot = b.shelf_location[0];
                              const matchedZone = [...SHELF_ZONES]
                                .sort((x, y) => y.prefix.length - x.prefix.length)
                                .find((z) => firstSlot.startsWith(z.prefix)) || SHELF_ZONES[0];
                              setActiveZone(matchedZone.id);
                              setSelectedSlotCode(firstSlot);
                              showToast(`✓ Located "${cleanName}" (${b.shelf_location.map(shelfCodeDisplay).join(", ")})`);
                            } else {
                              showToast(`✓ Located "${cleanName}" (Unplaced) — select shelf to place`);
                            }
                          }}
                          style={{
                            ...styles.saveBtn,
                            padding: "8px 16px",
                            fontSize: 13,
                          }}
                        >
                          {b.shelf_location.length > 0 ? `📍 Locate on Shelf (${shelfCodeDisplay(b.shelf_location[0])})` : "⚡ Select & Place"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ ...styles.modalActions, marginTop: 14 }}>
              <button
                type="button"
                onClick={() => setVariationPickerOpen(false)}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SHELF LINEUP AUDIT MODAL (Spirit Placements, not Unit Counts) ═══ */}
      {auditOpen && auditSlotCode && (
        <div className="mobile-sheet-backdrop" style={styles.modalBackdrop} onClick={() => setAuditOpen(false)}>
          <div className="mobile-sheet-content" style={{ ...styles.modalCard, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />

            <div style={{ ...styles.slotModalHeader, position: "sticky", top: 0, zIndex: 30, background: "var(--surface, #18181b)", paddingBottom: 10, marginBottom: 12, borderBottom: "1px solid var(--border, #27272a)" }}>
              <div>
                <h3 style={styles.modalTitle}>
                  🔍 Shelf Lineup Audit: Slot {auditSlotCode} ({shelfCodeDisplay(auditSlotCode)})
                </h3>
                <p style={styles.modalSub}>
                  Scan or verify which spirits belong on this shelf slot. (Total bottle unit quantities are tracked in Square Inventory; this updates the physical lineup on this shelf).
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAuditOpen(false)}
                style={styles.closeBtn}
                title="Cancel audit"
              >
                ✕
              </button>
            </div>

            {/* Audit Live Scan Bar */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="text"
                  value={auditManualQuery}
                  onChange={(e) => setAuditManualQuery(e.target.value)}
                  placeholder="Scan barcode or type spirit name…"
                  style={{ ...styles.modalInput, flex: 1 }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && auditManualQuery.trim()) {
                      handleScanAuditBarcode(auditManualQuery.trim());
                      setAuditManualQuery("");
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setAuditScannerOpen(true)}
                  style={styles.slotScanInputBtn}
                  title="Launch camera barcode scanner for lineup audit"
                >
                  📷 Scan Barcode
                </button>
              </div>

              {/* Audit Catalog Search Matches */}
              {auditManualQuery.trim().length >= 2 && (
                <div style={styles.searchResultsBox}>
                  {auditCatalogMatches.length === 0 ? (
                    <div style={{ padding: 10, fontSize: 12, color: "var(--muted)", textAlign: "center" }}>
                      No spirit found. Press Enter to submit barcode.
                    </div>
                  ) : (
                    auditCatalogMatches.map((b) => {
                      const alreadyScanned = auditScannedBottles.some((s) => s.id === b.id);
                      const isPour = Boolean(b.pour_price_cents && b.pour_price_cents > 0) || b.name.toLowerCase().includes("1 oz");
                      const isRetail = Boolean(b.retail_price_cents && b.retail_price_cents > 0) || Boolean(b.upc);
                      return (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => {
                            if (!alreadyScanned) {
                              setAuditScannedBottles((prev) => [b, ...prev]);
                              showToast(`✓ Added "${b.name}" to lineup`);
                            }
                            setAuditManualQuery("");
                          }}
                          disabled={alreadyScanned}
                          style={{
                            ...styles.searchResultItem,
                            ...(alreadyScanned ? { opacity: 0.5, cursor: "default" } : {}),
                          }}
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0, paddingRight: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>
                                {b.name}
                              </span>
                              {isRetail && b.retail_price_cents ? (
                                <span style={styles.miniRetailTag}>
                                  🍾 ${(b.retail_price_cents / 100).toFixed(2)} Bottle
                                </span>
                              ) : null}
                              {isPour && b.pour_price_cents ? (
                                <span style={styles.miniPourTag}>
                                  🥃 ${(b.pour_price_cents / 100).toFixed(2)} Pour
                                </span>
                              ) : null}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 11, color: "var(--muted)" }}>
                              <span>{b.distillery || b.category}</span>
                              {b.upc ? <span style={styles.miniUpcTag}>UPC: {b.upc}</span> : <span style={{ color: "#666", fontSize: 10 }}>No Barcode</span>}
                              {b.sku ? <span style={styles.miniSkuTag}>SKU: {b.sku}</span> : null}
                            </div>
                          </div>
                          <span style={alreadyScanned ? styles.badgeAlready : styles.badgeAssign}>
                            {alreadyScanned ? "In Lineup" : "+ Add to Lineup"}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Audit Live Comparison Summary */}
            {(() => {
              const expected = locationMap[auditSlotCode] || [];
              const scanned = auditScannedBottles;
              const missing = expected.filter((b) => !scanned.some((s) => s.id === b.id));
              const added = scanned.filter((s) => !expected.some((e) => e.id === s.id));
              const confirmed = expected.filter((e) => scanned.some((s) => s.id === e.id));

              return (
                <div>
                  <div style={styles.auditStatsRow}>
                    <div style={styles.auditStatCard}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: "#86efac" }}>{confirmed.length}</span>
                      <span style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase" }}>Verified</span>
                    </div>
                    <div style={styles.auditStatCard}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: "#93c5fd" }}>{added.length}</span>
                      <span style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase" }}>Newly Placed</span>
                    </div>
                    <div style={styles.auditStatCard}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: missing.length ? "#f87171" : "var(--muted)" }}>{missing.length}</span>
                      <span style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase" }}>Removed</span>
                    </div>
                    <div style={styles.auditStatCard}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: "var(--gold)" }}>{scanned.length}</span>
                      <span style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase" }}>Placed Lineup</span>
                    </div>
                  </div>

                  {/* Scanned Lineup List */}
                  <div style={{ ...styles.slotPlacedSection, maxHeight: 200, overflowY: "auto" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <label style={{ ...styles.label, marginBottom: 0 }}>Scanned Spirit Lineup for {auditSlotCode} ({scanned.length})</label>
                      {scanned.length === 0 && <span style={{ fontSize: 11, color: "var(--muted)", fontStyle: "italic" }}>Scan or search spirits above</span>}
                    </div>

                    {scanned.length === 0 ? (
                      <div style={styles.emptySlotBox}>No spirits scanned yet. Point your camera at bottle barcodes to build lineup.</div>
                    ) : (
                      <div style={styles.placedList}>
                        {scanned.map((b) => {
                          const isNew = !expected.some((e) => e.id === b.id);
                          return (
                            <div key={b.id} style={styles.placedItemRow}>
                              <div>
                                <div style={styles.placedName}>
                                  {isNew ? <span style={{ color: "#93c5fd", marginRight: 4 }}>✨</span> : <span style={{ color: "#86efac", marginRight: 4 }}>✅</span>}
                                  {b.name.replace(/ - 1 oz$/i, "")}
                                </div>
                                <div style={styles.placedMeta}>
                                  {b.distillery || b.category} &middot; {isNew ? "Newly assigned to slot" : "Verified on slot"}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setAuditScannedBottles((prev) => prev.filter((s) => s.id !== b.id))}
                                style={styles.removeSlotBtn}
                                title="Remove from this lineup"
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Missing Spirits Warning Box */}
                  {missing.length > 0 && (
                    <div style={styles.missingWarningBox}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#fca5a5", marginBottom: 4 }}>
                        ⚠️ {missing.length} previously assigned spirit{missing.length > 1 ? "s" : ""} will be unassigned from {auditSlotCode}:
                      </div>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>
                        {missing.map((m) => m.name.replace(/ - 1 oz$/i, "")).join(", ")}
                      </div>
                    </div>
                  )}

                    {/* Audit Notes Input */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ ...styles.label, fontSize: 11, marginBottom: 4 }}>
                        Audit / Restock Notes (Optional)
                      </label>
                      <input
                        type="text"
                        value={auditNotes}
                        onChange={(e) => setAuditNotes(e.target.value)}
                        placeholder="e.g. Discarded 1 empty bottle; restocked from storage..."
                        style={styles.modalInput}
                      />
                    </div>

                  <div style={styles.modalActions}>
                    <button
                      type="button"
                      onClick={() => setAuditOpen(false)}
                      style={styles.cancelBtn}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleApplyAudit}
                      disabled={auditSubmitting}
                      style={{ ...styles.saveBtn, minWidth: 160 }}
                    >
                      {auditSubmitting ? "Saving Audit…" : `💾 Save Shelf Audit (${scanned.length} Verified)`}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ═══ AUDIT HISTORY & UNREPLACED BOTTLE LOGS MODAL ═══ */}
      {auditHistoryOpen && (
        <div className="mobile-sheet-backdrop" style={styles.modalBackdrop} onClick={() => setAuditHistoryOpen(false)}>
          <div className="mobile-sheet-content" style={{ ...styles.modalCard, maxWidth: 580 }} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />

            <div style={{ ...styles.slotModalHeader, position: "sticky", top: 0, zIndex: 30, background: "var(--surface, #18181b)", paddingBottom: 10, marginBottom: 12, borderBottom: "1px solid var(--border, #27272a)" }}>
              <div>
                <h3 style={styles.modalTitle}>
                  📜 Shelf Audit & Relocation History
                </h3>
                <p style={styles.modalSub}>
                  {historySlotFilter ? `History for Slot ${historySlotFilter}` : "All recent venue audits & bottle movements"}
                </p>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {historySlotFilter && (
                  <button
                    type="button"
                    onClick={() => {
                      setHistorySlotFilter(null);
                      loadAuditHistory();
                    }}
                    style={styles.miniHistoryBtn}
                  >
                    View All Slots
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setAuditHistoryOpen(false)}
                  style={styles.closeBtn}
                  title="Close audit history"
                >
                  ✕
                </button>
              </div>
            </div>

            {auditHistoryLoading ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--muted)" }}>
                Loading audit logs from server…
              </div>
            ) : auditHistoryRecords.length === 0 ? (
              <div style={{ ...styles.emptySlotBox, padding: 20 }}>
                No audit records logged yet for this shelf slot. Run a shelf audit to start tracking history.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "65dvh", overflowY: "auto", paddingRight: 4 }}>
                {auditHistoryRecords
                  .filter((h) => !historySlotFilter || h.slot_code === historySlotFilter || h.from_slot === historySlotFilter || h.to_slot === historySlotFilter)
                  .map((h) => {
                    const isMove = h.action === "smart_shelf_move";
                    const isAudit = h.action === "shelf_audit";

                    return (
                      <div key={h.id} style={styles.auditRecordCard}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={isMove ? styles.auditBadgeMove : styles.auditBadgeAudit}>
                              {isMove ? "⚡ Owner Bottle Relocation" : `🔍 Slot ${h.slot_code || "Audit"}`}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)" }}>
                              {h.auditor_name || "Staff"}
                            </span>
                          </div>
                          <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "monospace" }}>
                            {new Date(h.timestamp).toLocaleString()}
                          </span>
                        </div>

                        {/* Audit Details */}
                        {isAudit && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", fontSize: 11 }}>
                              <span style={{ color: "#86efac", fontWeight: 700 }}>
                                ✅ {h.verified_count || 0} Verified Present
                              </span>
                              {Boolean(h.added_count && h.added_count > 0) && (
                                <span style={{ color: "#93c5fd", fontWeight: 700 }}>
                                  &middot; ✨ {h.added_count} Newly Placed
                                </span>
                              )}
                              {Boolean(h.missing_count && h.missing_count > 0) && (
                                <span style={{ color: "#fca5a5", fontWeight: 700 }}>
                                  &middot; ⚠️ {h.missing_count} Empty / Removed
                                </span>
                              )}
                            </div>

                            {/* Unreplaced / Missing Bottles List */}
                            {h.missing_bottles && h.missing_bottles.length > 0 && (
                              <div style={{ padding: "6px 10px", background: "rgba(239, 68, 68, 0.1)", borderRadius: 6, border: "1px solid rgba(239, 68, 68, 0.25)" }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "#fca5a5", marginBottom: 2 }}>
                                  ⚠️ Empty / Removed Spirits (Not on shelf):
                                </div>
                                <div style={{ fontSize: 11, color: "var(--text)" }}>
                                  {h.missing_bottles.map((m) => m.name.replace(/ - 1 oz$/i, "")).join(", ")}
                                </div>
                              </div>
                            )}

                            {/* Newly Added Bottles List */}
                            {h.added_bottles && h.added_bottles.length > 0 && (
                              <div style={{ padding: "6px 10px", background: "rgba(59, 130, 246, 0.1)", borderRadius: 6, border: "1px solid rgba(59, 130, 246, 0.25)" }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "#93c5fd", marginBottom: 2 }}>
                                  ✨ Newly Added to Shelf:
                                </div>
                                <div style={{ fontSize: 11, color: "var(--text)" }}>
                                  {h.added_bottles.map((a) => a.name.replace(/ - 1 oz$/i, "")).join(", ")}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Relocation Details */}
                        {isMove && (
                          <div style={{ padding: "6px 10px", background: "rgba(249, 175, 54, 0.08)", borderRadius: 6, border: "1px solid rgba(249, 175, 54, 0.25)" }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gold)", marginBottom: 2 }}>
                              "{h.bottle_name?.replace(/ - 1 oz$/i, "")}"
                            </div>
                            <div style={{ fontSize: 11, color: "var(--muted)" }}>
                              Moved from <span style={{ color: "#fff", fontWeight: 700 }}>{h.from_slot}</span> &rarr;{" "}
                              <span style={{ color: "var(--gold)", fontWeight: 700 }}>{h.to_slot}</span>
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        {h.notes && (
                          <div style={{ fontSize: 11, fontStyle: "italic", color: "var(--muted)", borderLeft: "2px solid var(--gold)", paddingLeft: 6 }}>
                            "{h.notes}"
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ SMART BOTTLE RELOCATION MODAL (OWNER-ONLY) ═══ */}
      {smartMoveBottle && isOwner && (
        <div className="mobile-sheet-backdrop" style={styles.modalBackdrop} onClick={() => setSmartMoveBottle(null)}>
          <div className="mobile-sheet-content" style={{ ...styles.modalCard, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />

            <div style={{ ...styles.slotModalHeader, position: "sticky", top: 0, zIndex: 30, background: "var(--surface, #18181b)", paddingBottom: 10, marginBottom: 12, borderBottom: "1px solid var(--border, #27272a)" }}>
              <div>
                <h3 style={styles.modalTitle}>⚡ Smart Spirit Relocation</h3>
                <p style={styles.modalSub}>Owner acknowledgment: Move bottle to a different shelf location</p>
              </div>
              <button
                type="button"
                onClick={() => setSmartMoveBottle(null)}
                style={styles.closeBtn}
                title="Cancel move"
              >
                ✕
              </button>
            </div>

            {/* Bottle Info */}
            <div style={{ padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 8, border: "1px solid var(--border)", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
                {smartMoveBottle.name.replace(/ - 1 oz$/i, "")}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                {smartMoveBottle.distillery || smartMoveBottle.category} &middot; Currently at <strong style={{ color: "#fff" }}>{smartMoveFromSlot}</strong>
              </div>
            </div>

            {/* Destination Slot Selection */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Destination Shelf Slot *</label>
              <input
                type="text"
                value={smartMoveToSlot}
                onChange={(e) => setSmartMoveToSlot(e.target.value)}
                placeholder="e.g. BAR-1A, RES-2B, LIB-4C, STOR-1"
                style={styles.modalInput}
              />
              <span style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>
                Enter the target slot code where this bottle is now physically located.
              </span>
            </div>

            {/* Move Notes */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Relocation Reason / Note (Optional)</label>
              <input
                type="text"
                value={smartMoveNotes}
                onChange={(e) => setSmartMoveNotes(e.target.value)}
                placeholder="e.g. Moved to Reserve front rail for tonight's VIP service"
                style={styles.modalInput}
              />
            </div>

            {/* Acknowledgment Alert */}
            <div style={{ padding: "8px 12px", background: "rgba(249, 175, 54, 0.08)", border: "1px solid rgba(249, 175, 54, 0.25)", borderRadius: 6, marginBottom: 14, fontSize: 11, color: "var(--muted)" }}>
              ⚡ <strong>Owner Audit Guarantee:</strong> This action will automatically unassign <strong style={{ color: "#fff" }}>{smartMoveFromSlot}</strong> and assign <strong style={{ color: "var(--gold)" }}>{smartMoveToSlot.toUpperCase() || "target slot"}</strong>, logging the relocation with your owner signature in venue audit logs.
            </div>

            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setSmartMoveBottle(null)}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteSmartMove}
                disabled={smartMoveSubmitting || !smartMoveToSlot.trim()}
                style={{ ...styles.saveBtn, minWidth: 160 }}
              >
                {smartMoveSubmitting ? "Relocating…" : "⚡ Acknowledge & Move"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ADD NEW BOTTLE MODAL ═══ */}
      {addBottleOpen && (
        <div className="mobile-sheet-backdrop" style={styles.modalBackdrop} onClick={() => setAddBottleOpen(false)}>
          <div className="mobile-sheet-content" style={{ ...styles.modalCard, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />

            <div style={{ ...styles.slotModalHeader, position: "sticky", top: 0, zIndex: 30, background: "var(--surface, #18181b)", paddingBottom: 10, marginBottom: 12, borderBottom: "1px solid var(--border, #27272a)" }}>
              <div>
                <h3 style={styles.modalTitle}>Add Bottle to Shelf Map</h3>
                <p style={styles.modalSub}>Create a new spirit entry and assign its physical location</p>
              </div>
              <button
                type="button"
                onClick={() => setAddBottleOpen(false)}
                style={styles.closeBtn}
                title="Close modal"
              >
                ✕
              </button>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Bottle Name *</label>
              <input
                type="text"
                value={newBottleName}
                onChange={(e) => setNewBottleName(e.target.value)}
                placeholder="e.g. Blanton's Gold Single Barrel"
                style={styles.modalInput}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginBottom: 12 }}>
              <div>
                <label style={styles.label}>Distillery</label>
                <input
                  type="text"
                  value={newBottleDistillery}
                  onChange={(e) => setNewBottleDistillery(e.target.value)}
                  placeholder="e.g. Buffalo Trace"
                  style={styles.modalInput}
                />
              </div>
              <div>
                <label style={styles.label}>Category</label>
                <select
                  value={newBottleCategory}
                  onChange={(e) => setNewBottleCategory(e.target.value)}
                  style={styles.modalInput}
                >
                  <option value="Bourbon">Bourbon</option>
                  <option value="Rye">Rye</option>
                  <option value="Scotch">Scotch</option>
                  <option value="Japanese">Japanese</option>
                  <option value="Irish">Irish</option>
                  <option value="Tequila">Tequila</option>
                  <option value="Mezcal">Mezcal</option>
                  <option value="Gin">Gin</option>
                  <option value="Rum">Rum</option>
                  <option value="Amaro">Amaro</option>
                  <option value="Liqueur">Liqueur</option>
                </select>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginBottom: 12 }}>
              <div>
                <label style={styles.label}>Shelf Location Code *</label>
                <input
                  type="text"
                  value={newBottleShelfCode}
                  onChange={(e) => setNewBottleShelfCode(e.target.value)}
                  placeholder="e.g. RES-1A, LIB-7D, STOR-1A"
                  style={styles.modalInput}
                />
              </div>
              <div>
                <label style={styles.label}>UPC Barcode</label>
                <input
                  type="text"
                  value={newBottleUpc}
                  onChange={(e) => setNewBottleUpc(e.target.value)}
                  placeholder="e.g. 085000000000"
                  style={styles.modalInput}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginBottom: 16 }}>
              <div>
                <label style={styles.label}>Pour Price ($)</label>
                <input
                  type="number"
                  step="0.50"
                  value={newBottlePourPrice}
                  onChange={(e) => setNewBottlePourPrice(e.target.value)}
                  placeholder="e.g. 18.00"
                  style={styles.modalInput}
                />
              </div>
              <div>
                <label style={styles.label}>Bottle Price ($)</label>
                <input
                  type="number"
                  step="1.00"
                  value={newBottlePrice}
                  onChange={(e) => setNewBottlePrice(e.target.value)}
                  placeholder="e.g. 120.00"
                  style={styles.modalInput}
                />
              </div>
            </div>

            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setAddBottleOpen(false)}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveNewBottle}
                disabled={saving || !newBottleName.trim()}
                style={styles.saveBtn}
              >
                {saving ? "Adding…" : "Save & Place on Shelf"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global & Slot Barcode Scanner Modal (rendered above all sheets & backdrops) */}
      <BarcodeScannerModal
        isOpen={scannerOpen}
        onClose={() => {
          setScannerOpen(false);
          setTargetSlotForScan(null);
        }}
        onScan={handleScanBarcode}
        title={targetSlotForScan ? `Scan Bottle to ${targetSlotForScan}` : "Scan Bottle to Find Shelf Location"}
      />

      {/* Dedicated Shelf Audit Scanner Modal */}
      <BarcodeScannerModal
        isOpen={auditScannerOpen}
        onClose={() => setAuditScannerOpen(false)}
        onScan={(code) => {
          setAuditScannerOpen(false);
          handleScanAuditBarcode(code);
        }}
        title={auditSlotCode ? `Audit Lineup for ${auditSlotCode}` : "Scan Shelf Bottle"}
      />
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "16px 12px 40px",
    background: "var(--bg, #0C0907)",
    color: "var(--text, #F7F5F0)",
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100vw",
    boxSizing: "border-box",
    overflowX: "hidden",
  },
  header: {
    marginBottom: 16,
  },
  title: {
    margin: "0 0 6px",
    fontSize: 22,
    fontWeight: 700,
    color: "var(--text)",
  },
  subtitle: {
    margin: 0,
    fontSize: 13,
    color: "var(--muted)",
  },
  toast: {
    padding: "10px 16px",
    borderRadius: 8,
    color: "#0D0B08",
    fontWeight: 700,
    fontSize: 13,
    marginBottom: 16,
  },

  /* ── Global Bottle Search Card ── */
  globalSearchCard: {
    background: "rgba(18, 14, 10, 0.85)",
    border: "1px solid rgba(249, 175, 54, 0.28)",
    borderRadius: 14,
    padding: "14px 12px",
    marginBottom: 16,
    boxShadow: "0 4px 24px rgba(0,0,0,0.45)",
    backdropFilter: "blur(12px)",
    width: "100%",
    boxSizing: "border-box",
    overflowX: "hidden",
  },
  globalSearchHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 12,
  },
  globalSearchTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 800,
    color: "var(--gold)",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  globalSearchScopeBadge: {
    fontSize: 10,
    fontWeight: 800,
    color: "var(--gold)",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    padding: "2px 8px",
    borderRadius: 6,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  globalSearchSub: {
    margin: "4px 0 0",
    fontSize: 12,
    color: "var(--muted)",
    lineHeight: 1.4,
  },
  globalScanBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 14px",
    background: "rgba(59, 130, 246, 0.15)",
    border: "1px solid #3b82f6",
    borderRadius: 8,
    color: "#93c5fd",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
  },
  globalSearchInputWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
  },
  globalSearchIcon: {
    position: "absolute",
    left: 14,
    fontSize: 16,
    color: "var(--gold)",
    pointerEvents: "none",
  },
  globalSearchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 84px 12px 40px",
    background: "rgba(0, 0, 0, 0.55)",
    border: "1px solid rgba(249, 175, 54, 0.35)",
    borderRadius: 10,
    color: "var(--text)",
    fontSize: 14,
    outline: "none",
    transition: "all 0.15s ease",
  },
  globalSearchInputActions: {
    position: "absolute",
    right: 8,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  inlineScanIconBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    height: 34,
    padding: 0,
    background: "rgba(249, 175, 54, 0.14)",
    border: "1px solid rgba(249, 175, 54, 0.4)",
    borderRadius: 8,
    color: "var(--gold)",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  globalSearchClearBtn: {
    background: "rgba(255,255,255,0.08)",
    border: "none",
    borderRadius: "50%",
    width: 22,
    height: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--muted)",
    fontSize: 12,
    cursor: "pointer",
  },
  globalFilterRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
  },
  globalFilterChip: {
    padding: "5px 11px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  globalFilterChipActive: {
    background: "rgba(249, 175, 54, 0.18)",
    borderColor: "var(--gold)",
    color: "var(--gold)",
  },
  globalFilterChipActiveOpen: {
    background: "rgba(39, 174, 96, 0.2)",
    borderColor: "#27ae60",
    color: "#86efac",
  },
  globalFilterChipActiveRetail: {
    background: "rgba(59, 130, 246, 0.2)",
    borderColor: "#3b82f6",
    color: "#93c5fd",
  },
  globalFilterChipActiveStorage: {
    background: "rgba(234, 179, 8, 0.2)",
    borderColor: "#eab308",
    color: "#fde047",
  },
  globalFilterChipActiveUnplaced: {
    background: "rgba(239, 68, 68, 0.18)",
    borderColor: "#ef4444",
    color: "#fca5a5",
  },
  globalSearchCountText: {
    fontSize: 12,
    color: "var(--gold)",
  },
  globalResultsBox: {
    marginTop: 14,
    paddingTop: 14,
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  globalEmptyState: {
    padding: "20px 16px",
    textAlign: "center",
    color: "var(--muted)",
    fontSize: 13,
    background: "rgba(0,0,0,0.25)",
    borderRadius: 8,
  },
  globalResultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))",
    gap: 10,
    maxHeight: 460,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 0,
    width: "100%",
    boxSizing: "border-box",
  },
  globalBottleCard: {
    background: "rgba(18, 14, 10, 0.95)",
    border: "1px solid rgba(249, 175, 54, 0.22)",
    borderRadius: 12,
    padding: "12px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    cursor: "pointer",
    boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflowWrap: "anywhere",
    overflowX: "hidden",
    transition: "all 0.15s ease",
  },
  globalBottleHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    width: "100%",
    boxSizing: "border-box",
  },
  globalBottleName: {
    fontSize: 15,
    fontWeight: 800,
    color: "var(--text)",
    lineHeight: 1.28,
    marginBottom: 3,
    wordBreak: "break-word",
  },
  globalBottleMeta: {
    fontSize: 12,
    color: "var(--muted)",
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  globalLocationRow: {
    paddingTop: 8,
    borderTop: "1px solid rgba(255,255,255,0.06)",
    width: "100%",
    boxSizing: "border-box",
  },
  globalLocationTitle: {
    fontSize: 11,
    fontWeight: 800,
    color: "var(--gold)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  globalCardFooter: {
    marginTop: 2,
    paddingTop: 8,
    borderTop: "1px solid rgba(255,255,255,0.06)",
    width: "100%",
    boxSizing: "border-box",
  },
  globalCardManageBtn: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 12px",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid rgba(249, 175, 54, 0.35)",
    borderRadius: 8,
    color: "var(--gold)",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    transition: "all 0.15s ease",
  },
  locationPillsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    width: "100%",
    boxSizing: "border-box",
  },
  jumpLocationBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 9px",
    borderRadius: 6,
    fontSize: 11,
    cursor: "pointer",
    border: "1px solid transparent",
    maxWidth: "100%",
    boxSizing: "border-box",
    wordBreak: "break-word",
    transition: "all 0.15s ease",
  },
  jumpLocationOpen: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.4)",
    color: "#86efac",
  },
  jumpLocationRetail: {
    background: "rgba(59, 130, 246, 0.15)",
    border: "1px solid rgba(59, 130, 246, 0.4)",
    color: "#93c5fd",
  },
  jumpLocationStorage: {
    background: "rgba(234, 179, 8, 0.15)",
    border: "1px solid rgba(234, 179, 8, 0.4)",
    color: "#fde047",
  },
  jumpLocationActive: {
    boxShadow: "0 0 10px rgba(249, 175, 54, 0.4)",
    borderColor: "var(--gold)",
  },
  viewingNowDot: {
    fontSize: 10,
    fontWeight: 800,
    color: "var(--gold)",
  },
  unplacedBadge: {
    fontSize: 11,
    fontWeight: 700,
    color: "#fca5a5",
    background: "rgba(239, 68, 68, 0.12)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    padding: "3px 8px",
    borderRadius: 6,
  },
  quickPlaceBtn: {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--gold)",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid var(--gold)",
    borderRadius: 6,
    padding: "3px 8px",
    cursor: "pointer",
  },
  areaSearchMatchPill: {
    fontSize: 10,
    fontWeight: 800,
    color: "#000",
    background: "var(--gold)",
    padding: "2px 7px",
    borderRadius: 6,
    boxShadow: "0 0 8px rgba(249, 175, 54, 0.4)",
    whiteSpace: "nowrap",
  },
  slotMatchBadge: {
    fontSize: 9,
    fontWeight: 800,
    color: "#000",
    background: "var(--gold)",
    padding: "1px 5px",
    borderRadius: 4,
    boxShadow: "0 0 6px rgba(249, 175, 54, 0.6)",
    letterSpacing: 0.5,
  },

  /* ── Purpose Segment Bar ── */
  purposeBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  purposeGroup: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  purposeBtn: {
    padding: "6px 12px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--muted)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  purposeBtnActive: {
    background: "rgba(249, 175, 54, 0.15)",
    borderColor: "var(--gold)",
    color: "var(--gold)",
  },
  purposeBtnActiveOpen: {
    background: "rgba(39, 174, 96, 0.18)",
    borderColor: "#27ae60",
    color: "#86efac",
  },
  purposeBtnActiveRetail: {
    background: "rgba(59, 130, 246, 0.18)",
    borderColor: "#3b82f6",
    color: "#93c5fd",
  },
  purposeBtnActiveBackStock: {
    background: "rgba(234, 179, 8, 0.18)",
    borderColor: "#eab308",
    color: "#fde047",
  },
  locatorTriggerBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    background: "rgba(59, 130, 246, 0.15)",
    border: "1px solid #3b82f6",
    borderRadius: 8,
    color: "#93c5fd",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
  },
  auditTriggerBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid var(--gold)",
    borderRadius: 8,
    color: "var(--gold)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  /* ── 4 Primary Physical Areas ── */
  primaryAreaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
    marginBottom: 12,
  },
  areaCard: {
    textAlign: "left",
    background: "rgba(255,255,255,0.02)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: "14px 16px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  areaCardActive: {
    borderColor: "var(--gold)",
    background: "rgba(249, 175, 54, 0.08)",
    boxShadow: "0 0 14px rgba(249, 175, 54, 0.12)",
  },
  areaCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  areaIcon: {
    fontSize: 18,
  },
  areaTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "var(--text)",
  },
  areaBadgeOpen: {
    fontSize: 10,
    fontWeight: 800,
    color: "#86efac",
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid #27ae60",
    padding: "2px 7px",
    borderRadius: 6,
    whiteSpace: "nowrap",
  },
  areaBadgeRetail: {
    fontSize: 10,
    fontWeight: 800,
    color: "#93c5fd",
    background: "rgba(59, 130, 246, 0.15)",
    border: "1px solid #3b82f6",
    padding: "2px 7px",
    borderRadius: 6,
    whiteSpace: "nowrap",
  },
  areaBadgeStorage: {
    fontSize: 10,
    fontWeight: 800,
    color: "#fde047",
    background: "rgba(234, 179, 8, 0.15)",
    border: "1px solid #eab308",
    padding: "2px 7px",
    borderRadius: 6,
    whiteSpace: "nowrap",
  },
  areaSub: {
    fontSize: 11,
    color: "var(--muted)",
    lineHeight: 1.3,
  },
  areaFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 4,
    paddingTop: 6,
    borderTop: "1px solid rgba(255,255,255,0.04)",
  },
  areaPlacedCount: {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--gold)",
  },
  areaSubCount: {
    fontSize: 11,
    color: "var(--muted)",
  },

  /* ── Sub-Shelves Bar ── */
  subShelfBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    padding: "10px 14px",
    background: "rgba(0,0,0,0.35)",
    border: "1px solid rgba(249, 175, 54, 0.2)",
    borderRadius: 10,
    marginBottom: 16,
  },
  subShelfLabel: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--gold)",
  },
  subShelfPillGroup: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    flex: 1,
  },
  subShelfPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    background: "rgba(255,255,255,0.02)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--muted)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    transition: "all 0.15s ease",
  },
  subShelfPillActive: {
    background: "rgba(249, 175, 54, 0.15)",
    borderColor: "var(--gold)",
    color: "var(--gold)",
    fontWeight: 700,
  },
  locIcon: {
    fontSize: 14,
  },
  locName: {
    whiteSpace: "nowrap",
  },
  locBadgeActive: {
    fontSize: 10,
    background: "var(--gold)",
    color: "#000",
    fontWeight: 800,
    padding: "1px 5px",
    borderRadius: 6,
  },
  locBadgeInactive: {
    fontSize: 10,
    background: "rgba(255,255,255,0.06)",
    color: "var(--muted)",
    fontWeight: 600,
    padding: "1px 5px",
    borderRadius: 6,
  },

  /* ── Main Card ── */
  mainCard: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: "16px 18px",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
  cardTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 700,
    color: "var(--text)",
  },
  cardDesc: {
    margin: 0,
    fontSize: 12,
    color: "var(--muted)",
  },
  searchWrap: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
    minWidth: 280,
  },
  viewToggleGroup: {
    display: "flex",
    background: "rgba(0,0,0,0.4)",
    borderRadius: 8,
    border: "1px solid var(--border)",
    overflow: "hidden",
  },
  viewToggleBtn: {
    padding: "6px 12px",
    background: "transparent",
    border: "none",
    color: "var(--muted)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  viewToggleActive: {
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold)",
    fontWeight: 700,
  },
  searchInput: {
    flex: "1 1 160px",
    padding: "8px 12px",
    background: "rgba(0,0,0,0.3)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--text)",
    fontSize: 13,
    outline: "none",
  },
  scanBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "8px 14px",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid var(--gold)",
    borderRadius: 8,
    color: "var(--gold)",
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "8px 14px",
    background: "var(--gold)",
    border: "none",
    borderRadius: 8,
    color: "#000",
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  statsStrip: {
    display: "flex",
    gap: 20,
    padding: "10px 14px",
    background: "rgba(0,0,0,0.3)",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.04)",
    marginBottom: 16,
  },
  statItem: {
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  },
  statVal: {
    fontSize: 15,
    fontWeight: 800,
    color: "#27ae60",
  },
  statLabel: {
    fontSize: 11,
    color: "var(--muted)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  gridHeaderCell: {
    height: 28,
  },
  gridColHeader: {
    textAlign: "center",
    fontSize: 11,
    fontWeight: 700,
    color: "var(--gold)",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    padding: "4px 0",
    background: "rgba(249, 175, 54, 0.05)",
    borderRadius: 6,
  },
  gridAisleGap: {
    width: 14,
    background: "repeating-linear-gradient(45deg, rgba(255,255,255,0.02), rgba(255,255,255,0.02) 2px, transparent 2px, transparent 6px)",
    borderRadius: 4,
  },
  gridRowLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rowLetterBadge: {
    fontSize: 14,
    fontWeight: 800,
    color: "var(--gold)",
    background: "rgba(249, 175, 54, 0.1)",
    border: "1px solid rgba(249, 175, 54, 0.2)",
    borderRadius: 6,
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  gridSlotCell: {
    minHeight: 88,
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "rgba(255,255,255,0.02)",
    textAlign: "left",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    transition: "all 0.15s ease",
  },
  gridSlotOccupied: {
    borderColor: "rgba(249, 175, 54, 0.4)",
    background: "rgba(249, 175, 54, 0.04)",
  },
  gridSlotEmpty: {
    borderStyle: "dashed",
    borderColor: "rgba(255,255,255,0.08)",
  },
  gridSlotSelected: {
    borderColor: "var(--gold)",
    boxShadow: "0 0 10px rgba(249, 175, 54, 0.3)",
  },
  slotHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  slotCodeText: {
    fontFamily: "monospace",
    fontSize: 11,
    fontWeight: 800,
    color: "var(--gold)",
  },
  slotCountBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: "1px 5px",
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold)",
    borderRadius: 4,
  },
  slotPillList: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    marginTop: 2,
  },
  slotBottlePill: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text)",
    background: "rgba(0,0,0,0.3)",
    padding: "2px 6px",
    borderRadius: 4,
    border: "1px solid rgba(255,255,255,0.04)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  miniPourTag: {
    fontSize: 9,
    fontWeight: 700,
    color: "#86efac",
  },
  miniRetailTag: {
    fontSize: 9,
    fontWeight: 700,
    color: "#93c5fd",
  },
  miniBackStockTag: {
    fontSize: 10,
  },
  backStockTagBadge: {
    display: "inline-block",
    padding: "1px 5px",
    background: "rgba(234, 179, 8, 0.15)",
    border: "1px solid rgba(234, 179, 8, 0.3)",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 700,
    color: "#fde047",
  },
  slotMoreBadge: {
    fontSize: 10,
    color: "var(--muted)",
    fontStyle: "italic",
    paddingLeft: 2,
  },
  slotEmptyHint: {
    fontSize: 11,
    color: "var(--muted)",
    fontStyle: "italic",
    marginTop: "auto",
    opacity: 0.6,
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid var(--border)",
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  tr: {
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
  td: {
    padding: "12px",
    verticalAlign: "middle",
  },
  typeBadgeOpen: {
    display: "inline-block",
    padding: "2px 6px",
    background: "rgba(39, 174, 96, 0.12)",
    border: "1px solid #27ae60",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    color: "#86efac",
  },
  typeBadgeRetail: {
    display: "inline-block",
    padding: "2px 6px",
    background: "rgba(59, 130, 246, 0.12)",
    border: "1px solid #3b82f6",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    color: "#93c5fd",
  },
  codeBadge: {
    display: "inline-block",
    padding: "2px 6px",
    background: "rgba(249, 175, 54, 0.1)",
    border: "1px solid rgba(249, 175, 54, 0.25)",
    borderRadius: 4,
    fontSize: 11,
    fontFamily: "monospace",
    fontWeight: 700,
    color: "var(--gold)",
    marginRight: 4,
  },
  inspectBtn: {
    padding: "6px 10px",
    background: "rgba(59, 130, 246, 0.12)",
    border: "1px solid #3b82f6",
    borderRadius: 6,
    color: "#93c5fd",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  reassignBtn: {
    padding: "6px 12px",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid var(--gold)",
    borderRadius: 6,
    color: "var(--gold)",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  /* ── Compact Shelf Zone Switcher Rail ── */
  roomFilterPill: {
    padding: "6px 2px",
    background: "rgba(255, 255, 255, 0.03)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: 8,
    color: "var(--muted, #a1a1aa)",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    whiteSpace: "nowrap",
    width: "100%",
  },
  roomFilterPillActive: {
    background: "rgba(249, 175, 54, 0.16)",
    borderColor: "var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
    boxShadow: "0 0 10px rgba(249, 175, 54, 0.2)",
  },
  compactZoneRailWrap: {
    marginBottom: 8,
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  compactZoneRail: {
    display: "flex",
    gap: 6,
    flexWrap: "nowrap",
    minWidth: "max-content",
    paddingBottom: 2,
  },
  compactZoneChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    background: "rgba(255, 255, 255, 0.03)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: 20,
    color: "var(--muted, #a1a1aa)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
  },
  compactZoneChipActive: {
    background: "rgba(249, 175, 54, 0.16)",
    borderColor: "var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
    boxShadow: "0 0 12px rgba(249, 175, 54, 0.25)",
  },
  zoneMatchDot: {
    fontSize: 10,
    fontWeight: 800,
    color: "#000",
    background: "var(--gold, #F9AF36)",
    padding: "1px 5px",
    borderRadius: 6,
  },
  compactZoneBadge: {
    fontSize: 10,
    fontWeight: 700,
    color: "var(--muted)",
    background: "rgba(255,255,255,0.06)",
    padding: "1px 6px",
    borderRadius: 10,
  },
  compactZoneBadgeActive: {
    fontSize: 10,
    fontWeight: 800,
    color: "#000",
    background: "var(--gold, #F9AF36)",
    padding: "1px 6px",
    borderRadius: 10,
  },

  /* ── Floor Status Strip (32px) ── */
  floorStatusStrip: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    background: "rgba(0,0,0,0.35)",
    borderBottom: "1px solid var(--border, #27272a)",
    flexWrap: "wrap",
  },
  miniFilterPill: {
    padding: "4px 8px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: 12,
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  miniFilterPillActive: {
    background: "rgba(249, 175, 54, 0.15)",
    borderColor: "var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
  },
  // flexShrink 0: on phones its icon buttons are 44px wide (Phase 4d), and a shrunk group hid the
  // second one behind overflow:hidden. No-op wherever the row fits.
  viewToggleGroupMini: {
    display: "flex",
    flexShrink: 0,
    background: "rgba(0,0,0,0.5)",
    borderRadius: 8,
    border: "1px solid var(--border, #27272a)",
    overflow: "hidden",
  },
  viewToggleBtnMini: {
    padding: "4px 8px",
    background: "transparent",
    border: "none",
    color: "var(--muted)",
    fontSize: 12,
    cursor: "pointer",
  },
  viewToggleActiveMini: {
    background: "rgba(249, 175, 54, 0.2)",
    color: "var(--gold)",
  },
  miniAddBtn: {
    padding: "4px 10px",
    background: "var(--gold, #F9AF36)",
    border: "none",
    borderRadius: 8,
    color: "#000",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
  },

  /* ── Viewport-Safe Bottom Sheet Modal ── */
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.82)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    zIndex: 9999,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    padding: 0,
  },
  modalCard: {
    background: "var(--surface, #18181b)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: "20px 20px 0 0",
    width: "100%",
    maxWidth: 580,
    maxHeight: "85dvh",
    overflowY: "auto",
    overscrollBehaviorY: "contain",
    padding: "16px 18px calc(24px + env(safe-area-inset-bottom, 0px))",
    boxShadow: "0 -10px 40px rgba(0,0,0,0.85)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  slotModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    gap: 12,
  },
  modalTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 800,
    color: "var(--gold)",
  },
  modalSub: {
    margin: "2px 0 0",
    fontSize: 11,
    color: "var(--muted)",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: "var(--muted, #a1a1aa)",
    fontSize: 18,
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  auditSlotBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "6px 10px",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid var(--gold)",
    borderRadius: 6,
    color: "var(--gold)",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  slotPlacedSection: {
    marginBottom: 16,
    padding: 12,
    background: "rgba(0,0,0,0.3)",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.05)",
  },
  emptySlotBox: {
    fontSize: 12,
    color: "var(--muted)",
    fontStyle: "italic",
    padding: "6px 0",
  },
  placedList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 6,
  },
  placedItemRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 10px",
    background: "rgba(255,255,255,0.02)",
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.04)",
  },
  placedName: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text)",
  },
  placedMeta: {
    fontSize: 11,
    color: "var(--muted)",
  },
  removeSlotBtn: {
    padding: "4px 8px",
    background: "rgba(224, 80, 64, 0.12)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    borderRadius: 6,
    color: "#E05040",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: "var(--muted)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  modalInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    background: "var(--bg, #09090b)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--text)",
    fontSize: 13,
    outline: "none",
  },
  searchResultsBox: {
    marginTop: 8,
    maxHeight: 260,
    overflowY: "auto",
    background: "var(--bg, #09090b)",
    border: "1px solid var(--border)",
    borderRadius: 8,
  },
  searchResultItem: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    background: "transparent",
    border: "none",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    textAlign: "left",
    cursor: "pointer",
  },
  miniUpcTag: {
    fontSize: 10,
    fontFamily: "monospace",
    color: "#60a5fa",
    background: "rgba(59, 130, 246, 0.12)",
    border: "1px solid rgba(59, 130, 246, 0.25)",
    padding: "2px 6px",
    borderRadius: 4,
  },
  miniSkuTag: {
    fontSize: 10,
    fontFamily: "monospace",
    color: "var(--muted, #8A7A66)",
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid var(--border, #2A1F14)",
    padding: "2px 6px",
    borderRadius: 4,
  },
  miniLocTag: {
    fontSize: 10,
    fontWeight: 600,
    color: "#f59e0b",
    background: "rgba(245, 158, 11, 0.12)",
    border: "1px solid rgba(245, 158, 11, 0.25)",
    padding: "2px 6px",
    borderRadius: 4,
  },
  badgeAssign: {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--gold)",
    background: "rgba(249, 175, 54, 0.1)",
    padding: "3px 8px",
    borderRadius: 4,
  },
  badgeAlready: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--muted)",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
  },
  cancelBtn: {
    padding: "8px 16px",
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--muted)",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  saveBtn: {
    padding: "8px 18px",
    background: "var(--gold)",
    border: "none",
    borderRadius: 8,
    color: "#000",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  slotScanInputBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 14px",
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid var(--gold, #F9AF36)",
    borderRadius: 8,
    color: "var(--gold, #F9AF36)",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    minHeight: 40,
    transition: "all 0.15s ease",
  },

  /* ── Shelf Lineup Audit Specific Styles ── */
  auditStatsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 8,
    marginBottom: 14,
  },
  auditStatCard: {
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.05)",
    borderRadius: 8,
    padding: "8px 6px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  missingWarningBox: {
    padding: "10px 12px",
    background: "rgba(224, 80, 64, 0.1)",
    border: "1px solid rgba(224, 80, 64, 0.3)",
    borderRadius: 8,
    marginBottom: 14,
  },

  /* ── Venue Locator Specific Styles ── */
  locatorBottleBanner: {
    padding: "12px 14px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 12,
  },
  backStorageStockBanner: {
    padding: "10px 14px",
    background: "rgba(234, 179, 8, 0.08)",
    border: "1px solid rgba(234, 179, 8, 0.25)",
    borderRadius: 8,
    marginBottom: 14,
  },
  locationPlacementCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 14px",
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(249, 175, 54, 0.25)",
    borderRadius: 8,
    gap: 10,
  },
  jumpSlotBtn: {
    padding: "6px 12px",
    background: "var(--gold)",
    border: "none",
    borderRadius: 6,
    color: "#000",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  addAnotherSlotBox: {
    padding: 12,
    background: "rgba(0,0,0,0.3)",
    border: "1px dashed rgba(255,255,255,0.1)",
    borderRadius: 8,
    marginTop: 10,
  },
  relatedBottleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 10px",
    background: "rgba(255,255,255,0.02)",
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.04)",
  },

  /* ── Audit History & Unreplaced Spirits Styles ── */
  miniHistoryBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 8px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: 6,
    color: "var(--text, #e4e4e7)",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
  },
  historySlotBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "6px 10px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid var(--border, #3f3f46)",
    borderRadius: 6,
    color: "var(--text, #e4e4e7)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  unreplacedWarningCard: {
    padding: "10px 12px",
    background: "rgba(239, 68, 68, 0.08)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: 8,
    marginBottom: 12,
  },
  unreplacedItemRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "5px 8px",
    background: "rgba(239, 68, 68, 0.12)",
    borderRadius: 6,
    border: "1px solid rgba(239, 68, 68, 0.2)",
    gap: 6,
  },
  restockQuickBtn: {
    padding: "3px 8px",
    background: "rgba(34, 197, 94, 0.2)",
    border: "1px solid rgba(34, 197, 94, 0.4)",
    borderRadius: 4,
    color: "#86efac",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  smartMoveBtn: {
    padding: "5px 9px",
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid rgba(249, 175, 54, 0.4)",
    borderRadius: 6,
    color: "var(--gold, #F9AF36)",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  auditRecordCard: {
    padding: "12px 14px",
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  auditBadgeAudit: {
    fontSize: 10,
    fontWeight: 800,
    padding: "2px 6px",
    borderRadius: 4,
    background: "rgba(59, 130, 246, 0.15)",
    color: "#93c5fd",
    border: "1px solid rgba(59, 130, 246, 0.3)",
    textTransform: "uppercase",
  },
  auditBadgeMove: {
    fontSize: 10,
    fontWeight: 800,
    padding: "2px 6px",
    borderRadius: 4,
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold, #F9AF36)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    textTransform: "uppercase",
  },
};
